// NOTA DE VENTA — puesto comercial C2.1 (slice 2A.1). El vendedor digita (cliente, productos,
// cantidades, presentación, descuento, precio manual si está autorizado) y el BACKEND decide: precio,
// escala (D-50), impuestos (D-14), crédito (D-108), aprobaciones, disponibilidad y reserva (1F-C).
// Cada cambio se PREVISUALIZA sin guardar (D-UI-1); F8 solo recalcula de inmediato; F9 guarda y abre
// el resumen para confirmar. Geometría C2.1: cabecera 44+46 · situación comercial 62 · grilla 70 % +
// panel contextual 30 % · totales 106 · atajos 52. La nota NO administra despachos (C2.1 §1).
// Todo por teclado: F2 cliente → código o F3 → cantidad → Enter → … → F9 → Enter.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { ErrorApi, api, nuevaClaveIdempotencia, pedirArchivo } from '../api/cliente';
import {
  claves,
  useContextoComercial,
  useDisponibilidad,
  useFichaCliente,
  useListasPrecio,
  useNota,
  usePagoNota,
  usePreciosLista,
  usePrevisualizacion,
  useUbicaciones,
  useVendedores,
  type EstadoCalculo,
  useConsumidorFinal,
  useProximoFolio,
  useProximoNumeroNota,
  type ProximoFolio,
  useTurnoActual,
} from '../api/consultas';
import { CampoFolio } from './CampoFolio';
import {
  UBICACIONES_DE_VENTA,
  CANALES,
  type ClienteEncontrado,
  type CondicionPago,
  type Nota,
  type Previsualizacion,
  type ResultadoConfirmar,
  type ResumenNota,
  type Totales as TotalesApi,
  type CargoNota,
  type CambioVendedor,
  type MotivoCambioVendedor,
  type ReciboRegistrado,
  type EntregaNota,
  type NotaListada,
} from '../api/contratos';
import { NotasPorRetirar, useNotasPorRetirar } from './NotasPorRetirar';
import { useIntentos } from '../api/idempotencia';
import {
  EstadoCredito,
  EstadoNota,
  EstadoOperacion,
  PanelError,
  type Operacion,
} from '../comun/Estados';
import { EstadoPago } from '../comun/EstadoPago';
import { Modal } from '../comun/Modal';
import {
  decimal,
  esCero,
  fecha,
  fechaHora,
  NOMBRE_CANAL,
  NOMBRE_CONDICION,
  pesos,
  rut,
  STOCK_POR_REVISAR,
} from '../comun/formato';
import { MOTIVOS_CREDITO, NOMBRE_AMBITO } from '../errores/catalogo';
import { Imprimir } from '../comun/Imprimir';
import { NotaImpresa, type DatosNotaImpresa } from './NotaImpresa';
import { resolverCodigo } from './codigo';
import { useSesion } from '../sesion/SesionContexto';
import { useSalud } from '../shell/salud';
import { useAtajos } from '../shell/atajos';
import {
  borradorDesdeNota,
  borradorNuevo,
  clienteDesdeBusqueda,
  cuerpoCrear,
  cuerpoEditar,
  cuerpoPrevisualizar,
  problemasDe,
  reducirBorrador,
  type ProblemaBorrador,
} from './borrador';
import { ClienteNoExiste, ClienteNuevo, type EntidadExistente } from '../clientes/ClienteNuevo';
import { BuscadorCliente } from './BuscadorCliente';
import { CobrarNota } from './CobrarNota';
import { EsperandoSinPago, useAutorizacionSinPago } from './SinPagoMeson';
import { PagoMeson, composicionVuelto, estadoPago } from './PagoMeson';
import { AbrirTurno } from '../caja/MiTurno';
import { imprimirPdf } from '../comun/imprimirPdf';
import { filaNueva, medioDe, type Fila } from '../comun/medios';
import { useDocumentosGuardados } from '../consultas/DocumentosGuardados';
import { BuscadorProducto } from './BuscadorProducto';
import {
  NOMBRE_MOTIVO_VENDEDOR,
  canalPorDefecto,
  direccionPorDefecto,
  direccionesElegibles,
  tipoDireccion,
  hoyIso,
  pideMotivoVendedor,
  textoCondicion,
  textoDireccion,
  valorCondicion,
  vencimiento,
} from './comercial';
import { GrillaLineas, type GrillaLineasRef } from './GrillaLineas';
import { PanelContexto, useNombreComuna, type PestanaContexto } from './PanelContexto';
import { useNombreActor } from './Paneles';
import { ImprimirVale } from './ValeVenta';
import { clavesDocumentos, useDocumentosEmitidos } from '../dte/documentosEmitidos';
import { imprimirDte as imprimirDocumento, tituloDte, verPdfDte } from '../dte/pdfDte';
import { MOTIVOS_VALE, type DocumentoNota, type MotivoVale } from '../api/contratos';

/**
 * Documento directo (pedido de Rodrigo, 2026-10-09, como en Random): factura, boleta o guía sin una
 * NV previa. Por dentro es la misma nota con el documento ya elegido; al confirmarla queda lista para
 * emitir (la guía pasa al despacho). En preproducción no se despacha ni se emite.
 */
export type DocumentoDirecto = 'FACTURA' | 'BOLETA' | 'GUIA';
export const NOMBRE_DIRECTO: Record<DocumentoDirecto, string> = {
  FACTURA: 'Factura directa',
  BOLETA: 'Boleta directa',
  GUIA: 'Guía de despacho directa',
};

export interface PropsNotaVenta {
  activa: boolean;
  /** Documento directo: la nota parte con ese documento y termina «lista para emitir». */
  directo?: DocumentoDirecto;
  notaId?: string;
  /** Nota nueva para un cliente ya elegido (desde la consulta de cliente, C2-5). */
  clienteInicialId?: string;
  /** La ubicación desde la que se vende (consignación: «facturar lo consumido»). */
  ubicacionInicialId?: string;
  alTitular: (titulo: string, clave: string, params: Record<string, string>) => void;
  alEnsuciar: (sucia: boolean) => void;
  alAbrirNota?: (notaId: string, numeroVisible: string) => void;
  /** Abre la ficha completa del cliente (Alt+F o el botón del panel Cliente). */
  alAbrirFicha?: (entidadId: string, nombre: string) => void;
  /** Solo perfiles de bodega/logística (Alt+P): el puesto comercial no administra despachos. */
  alAbrirDespacho?: (notaId: string, numeroVisible: string) => void;
  /** La ficha de la factura o boleta emitida (2026-10-26). */
  alAbrirDte?: (documentoId: string, titulo: string) => void;
  /** La nota terminó: la pestaña se limpia para partir la siguiente (la abre el Shell). */
  /** Punto de venta (Caja › Punto de venta): consumidor final, F10 = cobrar y emitir. */
  meson?: boolean;
  /** Parte una nota nueva en la misma pestaña; con `guardada`, avisa que esa quedó como borrador. */
  alNuevaNota?: (guardada?: { notaId: string; numero: string }) => void;
  /** Punto de venta: toma una nota confirmada del cliente (por retirar) en esta misma pestaña. */
  alTomarNota?: (notaId: string, numeroVisible: string, deGuardados?: boolean) => void;
  /**
   * La nota se TOMÓ de los guardados (pedido de Rodrigo, 2026-10-23): un guardado es un documento en
   * construcción; tomado, sale de los guardados y solo sigue guardado si se vuelve a guardar. Si se
   * limpia la pantalla o se cierra la pestaña sin guardarlo, el borrador se descarta.
   */
  tomadaDeGuardados?: boolean;
  /** La nota que se acaba de guardar como borrador antes de limpiar la pantalla. */
  guardadaRecien?: { notaId: string; numero: string };
}

type Dialogo =
  | { tipo: 'cliente'; texto?: string }
  | {
      tipo: 'cliente-no-existe';
      rut: string;
      dv: string | null;
      existente: EntidadExistente | null;
    }
  | { tipo: 'cliente-nuevo'; rut: string; dv: string | null }
  | { tipo: 'producto'; texto: string }
  | { tipo: 'otros' }
  | { tipo: 'observacion' }
  | { tipo: 'cobrar' }
  | { tipo: 'emitir-directa' }
  | { tipo: 'confirmar' }
  | { tipo: 'motivo'; accion: 'aprobar' | 'rechazar' | 'reservar' | 'anular' | 'anular-guardar' }
  | { tipo: 'quitar'; clave: string; texto: string }
  | { tipo: 'vendedor'; vendedorId: string }
  | null;

/** Se pagan en el acto: cambiar a una de estas no pide permiso (lo controlado es el crédito). */
const CONDICIONES_EN_EL_ACTO: readonly string[] = ['contado', 'transferencia', 'cheque_al_dia'];

const ESTADO_CALCULO: Record<EstadoCalculo, { texto: string; tono: string } | null> = {
  incompleto: null,
  cambio: { texto: 'Cambió desde el último cálculo', tono: 'aviso' },
  calculando: { texto: 'Calculando…', tono: 'neutro' },
  actualizado: { texto: 'Actualizado', tono: 'ok' },
  error: { texto: 'No se pudo calcular', tono: 'malo' },
};

/** Etiqueta de campo con su marca: lápiz (editable con permiso) o candado (automático), y tooltip. */
/**
 * Rótulo de un dato de la cabecera. Lo que se puede cambiar se reconoce por el CAMPO (borde y
 * flecha); lo fijo es texto plano. `marca` queda como explicación (title), sin iconos.
 */
/**
 * El número de la nota, como en Random: en una nota nueva se ve el que vendría (se asigna al guardar);
 * escribiendo otro número y Enter se abre ESA nota (en su pestaña). Esc vuelve al número de esta.
 */
function IrANumero({
  empresaId,
  actual,
  proximo,
  alAbrir,
}: {
  empresaId: string;
  actual: string | null;
  proximo: string | null;
  alAbrir?: (notaId: string, numeroVisible: string) => void;
}) {
  const [texto, setTexto] = useState(actual ?? '');
  const [aviso, setAviso] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const ir = async () => {
    const numero = texto.replace(/\D/g, '').replace(/^0+/, '');
    if (!numero || !alAbrir) return;
    if (actual && actual.replace(/\D/g, '').replace(/^0+/, '') === numero) return;
    setBuscando(true);
    setAviso(null);
    try {
      const r = await api.get<{ items: { id: string; numeroVisible: string }[] }>('/ventas/notas', {
        query: { empresaId, numero, tamano: '1' },
      });
      const encontrada = r.items[0];
      if (!encontrada) setAviso(`No existe la nota N° ${numero}`);
      else {
        alAbrir(encontrada.id, encontrada.numeroVisible);
        setTexto(actual ?? '');
      }
    } catch {
      setAviso('No se pudo buscar la nota');
    } finally {
      setBuscando(false);
    }
  };
  return (
    <div className="ir-numero">
      <input
        aria-label="Número NV"
        className={`v ${actual ? '' : 'nueva'}`}
        data-proximo={proximo ?? ''}
        value={texto}
        placeholder={proximo ?? 'Nota nueva'}
        title={
          actual
            ? 'Escribe otro número y Enter para abrir esa nota'
            : 'Número que vendría: se asigna al guardar. Escribe otro número y Enter para abrir esa nota'
        }
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => {
          setTexto(e.target.value);
          setAviso(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void ir();
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            setTexto(actual ?? '');
            setAviso(null);
          }
        }}
        disabled={buscando}
      />
      {!actual && proximo && !texto && (
        <span className="ayuda" title="Se asigna al guardar">
          próximo
        </span>
      )}
      {aviso && (
        <span className="ayuda tono-malo" role="alert">
          {aviso}
        </span>
      )}
    </div>
  );
}

/** Documento de la venta en el punto de venta: los tributarios y el vale (0109, no es DTE). */
type DocumentoPos = 'BOLETA' | 'FACTURA' | 'VALE';
const NOMBRE_DOC_POS: Record<DocumentoPos, string> = {
  BOLETA: 'Boleta',
  FACTURA: 'Factura',
  VALE: 'Vale',
};

/**
 * Punto de venta: el documento de la venta (Boleta / Factura / Vale, Alt+T alterna) y el folio
 * que llevaría. El folio no se reserva: se asigna al emitir, y si otra caja emite antes, sale el
 * siguiente (igual que el correlativo que Random muestra al abrir la boleta).
 */
function DocumentoMeson({
  documento,
  folio,
  cargando,
  editable,
  consumidorFinal,
  elegir,
  empresaId,
  alAbrirNota,
  conVale = false,
  motivoVale = 'VENTA',
  alMotivoVale,
}: {
  empresaId?: string;
  alAbrirNota?: (notaId: string, titulo: string) => void;
  documento: DocumentoPos;
  folio: ProximoFolio | undefined;
  cargando: boolean;
  editable: boolean;
  consumidorFinal: boolean;
  elegir: (d: DocumentoPos) => void;
  /** Con el permiso de emitir vales (0109): la tercera opción. */
  conVale?: boolean;
  motivoVale?: MotivoVale;
  alMotivoVale?: (m: MotivoVale) => void;
}) {
  const opciones: DocumentoPos[] = conVale ? ['BOLETA', 'FACTURA', 'VALE'] : ['BOLETA', 'FACTURA'];
  return (
    <div className="documento-meson" aria-label="Documento tributario" data-documento={documento}>
      <span className="etiqueta">Documento (Alt+T)</span>
      <div className="segmentos" role="radiogroup" aria-label="Tipo de documento">
        {opciones.map((d) => (
          <button
            key={d}
            type="button"
            role="radio"
            aria-checked={documento === d}
            className={documento === d ? 'activo' : ''}
            disabled={!editable}
            onClick={() => elegir(d)}
          >
            {NOMBRE_DOC_POS[d]}
          </button>
        ))}
      </div>
      {documento === 'VALE' && (
        <>
          <label className="campo motivo-vale">
            <span className="etiqueta">Motivo del vale</span>
            <select
              aria-label="Motivo del vale"
              value={motivoVale}
              disabled={!editable}
              onChange={(e) => alMotivoVale?.(e.target.value as MotivoVale)}
            >
              {MOTIVOS_VALE.map(([v, n]) => (
                <option key={v} value={v}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <span className="aviso-linea tono-aviso" data-vale-aviso>
            Vale interno: no es boleta ni factura y no va al SII.
          </span>
        </>
      )}
      {empresaId && documento !== 'VALE' && (
        <CampoFolio
          empresaId={empresaId}
          tipoDte={documento === 'BOLETA' ? 39 : 33}
          proximo={folio}
          cargando={cargando}
          {...(alAbrirNota ? { alAbrirNota } : {})}
        />
      )}
      {documento === 'FACTURA' && consumidorFinal && (
        <span className="aviso-linea tono-aviso">
          La factura necesita el RUT del cliente: F2 para elegirlo.
        </span>
      )}
    </div>
  );
}

/**
 * El cliente de la nota en UN solo campo, como «Empresa» en Random: un clic (o Enter) lo abre para
 * digitar el código (RUT sin dígito, o con él); Enter lo trae directo. Enter con el campo vacío abre
 * la búsqueda (el «segundo Enter»), y un nombre + Enter la abre filtrada. Un código que no existe
 * ofrece crear al cliente. Esc vuelve a mostrar el cliente que había.
 */
function CampoCliente({
  empresaId,
  cliente,
  habilitado,
  editarAlInicio,
  tituloBloqueado,
  alElegir,
  alBuscar,
  alNoExiste,
  alTabulador,
}: {
  empresaId: string;
  cliente: { rut: string | null; razonSocial: string } | null;
  habilitado: boolean;
  /** Nota nueva sin cliente: el cursor parte digitando el código (como Random). */
  editarAlInicio: boolean;
  tituloBloqueado: string;
  alElegir: (c: ClienteEncontrado) => void;
  alBuscar: (texto: string) => void;
  /**
   * El código no es de ningún cliente: «rut» sin dígito, «dv» si se digitó, y la entidad del grupo
   * que ya tiene ese RUT (si la hay).
   */
  alNoExiste: (rut: string, dv: string | null, existente: EntidadExistente | null) => void;
  /** Tab desde el cliente salta directo a las líneas de detalle (pedido de Rodrigo, 2026-10-23). */
  alTabulador: () => void;
}) {
  const [editando, setEditando] = useState(habilitado && editarAlInicio);
  const [texto, setTexto] = useState('');
  const [buscando, setBuscando] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editando) ref.current?.focus();
  }, [editando]);
  const cerrar = () => {
    setEditando(false);
    setTexto('');
  };
  const ir = async () => {
    const t = texto.trim();
    if (!t) {
      cerrar();
      return alBuscar('');
    }
    // Código: solo dígitos, con puntos, guion o dígito verificador opcionales (18.760.765-K).
    const m = /^(\d{1,9})(?:-?([\dkK]))?$/.exec(t.replace(/\./g, ''));
    if (!m) {
      cerrar();
      return alBuscar(t);
    }
    const cuerpo = m[1]!;
    // Sin guion no se sabe si el último dígito es el verificador: se prueba con y sin él.
    const candidatos = m[2]
      ? [cuerpo]
      : cuerpo.length >= 8
        ? [cuerpo, cuerpo.slice(0, -1)]
        : [cuerpo];
    setBuscando(true);
    try {
      for (const c of [...new Set(candidatos)]) {
        const filas = await api.get<ClienteEncontrado[]>('/entidades', {
          query: { empresaId, rol: 'cliente', texto: c, limite: '10' },
        });
        const exacto = filas.find((f) => f.rut === c);
        if (exacto) {
          cerrar();
          return alElegir(exacto);
        }
      }
      // ¿Está en el grupo (otra empresa, o proveedor)? Entonces no se crea: se hace cliente.
      for (const c of [...new Set(candidatos)]) {
        const filas = await api.get<ClienteEncontrado[]>('/entidades', {
          query: { texto: c, limite: '10' },
        });
        const e = filas.find((f) => f.rut === c);
        if (e) {
          cerrar();
          return alNoExiste(c, e.dv, { id: e.id, razonSocial: e.razon_social });
        }
      }
      cerrar();
      // Con guion el dígito es explícito; sin él, el código es el RUT completo sin dígito.
      alNoExiste(cuerpo, m[2] ? m[2].toUpperCase() : null, null);
    } catch {
      cerrar();
      alBuscar(t);
    } finally {
      setBuscando(false);
    }
  };
  if (editando)
    return (
      <input
        ref={ref}
        aria-label="Código cliente"
        className="selector-cliente codigo-cliente"
        placeholder="Código o RUT y Enter · Enter sin nada: buscar por nombre"
        value={texto}
        disabled={buscando}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={() => !buscando && cerrar()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey && texto.trim())) {
            // Tab con un código digitado busca igual que Enter; al elegir, el cursor va a las líneas.
            e.preventDefault();
            void ir();
          } else if (e.key === 'Tab' && !e.shiftKey && cliente) {
            e.preventDefault();
            cerrar();
            alTabulador();
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            cerrar();
          }
        }}
      />
    );
  return (
    <button
      type="button"
      className="selector-cliente"
      disabled={!habilitado}
      onClick={() => setEditando(true)}
      onKeyDown={(e) => {
        if (e.key === 'Tab' && !e.shiftKey && cliente && habilitado) {
          e.preventDefault();
          alTabulador();
        }
      }}
      title={
        habilitado ? 'Clic o Enter: digitar el código del cliente · F2: buscar' : tituloBloqueado
      }
    >
      {cliente ? (
        <>
          <span className="rut">{rut(cliente.rut)}</span>
          <span className="razon">{cliente.razonSocial}</span>
        </>
      ) : (
        <span className="apagado">Código o nombre del cliente… (F2)</span>
      )}
    </button>
  );
}

/**
 * Despacho o retiro en el local (pedido de Rodrigo, 2026-10-10). En Random el retiro iba en la
 * observación y a veces la nota salía igual a despacho; aquí una nota «Retira en local» nunca entra a
 * «NV por despachar» ni a una OD.
 */
function SelectorEntrega({
  valor,
  habilitado,
  alElegir,
}: {
  valor: EntregaNota;
  habilitado: boolean;
  alElegir: (e: EntregaNota) => void;
}) {
  return (
    <span className="segmentos entrega" role="radiogroup" aria-label="Entrega">
      {(['DESPACHO', 'RETIRO'] as const).map((e) => (
        <button
          key={e}
          type="button"
          role="radio"
          aria-checked={valor === e}
          className={valor === e ? 'activo' : ''}
          disabled={!habilitado}
          onClick={() => valor !== e && alElegir(e)}
        >
          {e === 'DESPACHO' ? 'Despacho' : 'Retira en local'}
        </button>
      ))}
    </span>
  );
}

function Etiqueta({
  texto,
  marca,
  tip,
  tecla,
  extra,
}: {
  texto: string;
  marca?: 'edita' | 'auto';
  tip?: string;
  tecla?: string;
  extra?: ReactNode;
}) {
  return (
    <span className="etiqueta-c21" title={tip ?? (marca === 'auto' ? 'Automático' : undefined)}>
      {texto}
      {tecla && <kbd className="tecla-campo">{tecla}</kbd>}
      {extra}
    </span>
  );
}

export function NotaVenta({
  activa,
  notaId: notaIdInicial,
  clienteInicialId: clienteInicialIdProp,
  ubicacionInicialId,
  meson: mesonProp = false,
  alTitular,
  alEnsuciar,
  alAbrirNota,
  alAbrirFicha,
  alAbrirDespacho,
  alAbrirDte,
  alNuevaNota,
  alTomarNota,
  guardadaRecien,
  tomadaDeGuardados = false,
  directo,
}: PropsNotaVenta) {
  const { empresa, puede, usuario } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  // Punto de venta (mesón): parte con el consumidor final; F2 lo cambia.
  const consumidor = useConsumidorFinal(
    empresaId,
    mesonProp && !clienteInicialIdProp && !notaIdInicial,
  );
  const clienteInicialId =
    clienteInicialIdProp ??
    (mesonProp && !notaIdInicial ? (consumidor.data ?? undefined) : undefined);
  const comuna = useNombreComuna();
  const ubicaciones = useUbicaciones(empresaId);
  const deVenta = useMemo(
    () =>
      (ubicaciones.data ?? []).filter(
        (u) =>
          (u.tipo &&
            UBICACIONES_DE_VENTA.includes(u.tipo) &&
            // Consignación: se vende desde ella solo al facturar lo consumido de ese cliente.
            u.tipo !== 'CONSIGNACION') ||
          u.id === ubicacionInicialId,
      ),
    [ubicaciones.data, ubicacionInicialId],
  );
  const { roles } = useSesion();
  const [b, despachar] = useReducer(reducirBorrador, undefined, () => {
    const nuevo = borradorNuevo(mesonProp ? 'MESON' : canalPorDefecto(roles));
    // Directo: el documento ya viene elegido (la guía se factura después: documento FACTURA).
    // Factura o boleta directa: el cliente se lleva la mercadería (retiro en el local), así que al
    // enviarse el DTE queda despachada sola (2026-10-23). La guía sigue siendo despacho.
    // Venta directa (2026-10-23): ni el punto de venta ni la factura o boleta directa son una nota de
    // venta para quien opera; quedan marcadas y no se listan como pedido.
    return directo
      ? {
          ...nuevo,
          documento: directo === 'BOLETA' ? ('BOLETA' as const) : ('FACTURA' as const),
          ...(directo !== 'GUIA' ? { entrega: 'RETIRO' as const, ventaDirecta: true } : {}),
        }
      : mesonProp
        ? { ...nuevo, ventaDirecta: true }
        : nuevo;
  });
  // Una nota hecha en el punto de venta (canal Mesón) se retoma como punto de venta aunque se abra
  // desde la consulta o los guardados (pedido de Rodrigo, 2026-10-13).
  const meson = mesonProp || b.canal === 'MESON';
  // La pestaña queda marcada como punto de venta: «Nueva venta» sigue en el punto de venta.
  const salud = useSalud();
  const sinEmision = salud.data?.modoOperacion === 'VALIDACION_OPERACIONAL';
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [imprimirNv, setImprimirNv] = useState(false);
  const [masAbierto, setMasAbierto] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [problemas, setProblemas] = useState<ProblemaBorrador[]>([]);
  const [resultado, setResultado] = useState<ResultadoConfirmar | null>(null);
  /** La nota TERMINÓ (confirmada o enviada a firma): Enter limpia la pestaña para la siguiente. */
  const [terminada, setTerminada] = useState<ResultadoConfirmar | null>(null);
  /** Cobrar desde «terminada»: al cerrar el cobro, la pestaña queda lista para otra nota. */
  const [nuevaTrasCobro, setNuevaTrasCobro] = useState(false);
  /** Punto de venta: F10 confirma, cobra y pide la boleta/factura en el acto. */
  const [cobrarTrasConfirmar, setCobrarTrasConfirmar] = useState(false);
  /**
   * Punto de venta sin caja o sin pago (Rodrigo, 2026-10-24): al grabar sin turno se ofrece abrir la
   * caja ahí mismo; sin pago, emitir pide el permiso ventas.documento.sin_pago o la firma SIN_PAGO.
   */
  const [avisoCaja, setAvisoCaja] = useState<null | 'abrir' | 'sin-pago' | 'factura'>(null);
  /** Se avisó que faltaban datos para la factura: al volver, la nota se graba de nuevo (con la ficha al día). */
  const refrescarReceptor = useRef(false);
  /** La venta que se graba ahora sale sin pago (se confirma declarándolo y se emite sin recibo). */
  const sinPagoMeson = useRef(false);
  /** Las firmas que pide la venta (de la última previsualización), para «Grabar y pagar». */
  const requeridosMeson = useRef<readonly string[]>([]);
  const [reciboMeson, setReciboMeson] = useState<ReciboRegistrado | null>(null);
  /** El documento con que salió (o sale) la venta del punto de venta. */
  const [docVentaMeson, setDocVentaMeson] = useState<DocumentoNota | null>(null);
  /** Esperando en pantalla la autorización en línea para emitir sin pago (0122). */
  const [esperandoSinPago, setEsperandoSinPago] = useState(false);
  const [ventaMeson, setVentaMeson] = useState<null | {
    recibo: ReciboRegistrado | null;
    documento: 'EMITIENDO' | 'SOLICITADO' | 'BLOQUEADO_VALIDACION' | 'ERROR';
    error?: unknown;
    /** El DTE pedido (boleta/factura), para imprimirlo al terminar. */
    dteId?: string;
    /** 0109: el vale emitido (no es DTE). */
    valeId?: string;
    valeNumero?: string;
  }>(null);
  /** Nota tomada de los guardados: si se suelta sin volver a guardarla, se descarta. */
  const tomada = useRef({ activa: tomadaDeGuardados, reguardada: false });
  const ultimaNota = useRef<{
    notaId: string | null;
    version: number | null;
    estado: string | null;
  }>({ notaId: null, version: null, estado: null });
  const soltarTomada = () => {
    const t = tomada.current;
    const u = ultimaNota.current;
    if (!t.activa || t.reguardada || !u.notaId || u.estado !== 'BORRADOR' || u.version === null)
      return;
    t.activa = false;
    void api
      .post(
        `/ventas/notas/${u.notaId}/descartar`,
        { version: u.version, motivo: 'Tomada de los guardados y no se volvió a guardar' },
        { idempotencia: `descartar-tomada-${u.notaId}-${u.version}` },
      )
      .catch(() => undefined)
      .then(() =>
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'documentos', 'guardados'] }),
      );
  };
  // Cerrar la pestaña la desmonta: lo tomado y no vuelto a guardar se descarta.
  useEffect(() => () => soltarTomada(), []);
  /** Punto de venta (como Random): los medios de pago en la misma pantalla de la venta. */
  const [filasPago, setFilasPago] = useState<Fila[]>([filaNueva('')]);
  /** «Grabar e imprimir»: al confirmar, el cobro sale de los pagos de la pantalla (sin diálogo). */
  const pagoEnPantalla = useRef(false);
  const [verPendientes, setVerPendientes] = useState(false);
  const [errorImpresion, setErrorImpresion] = useState<unknown>(null);
  const [op, setOp] = useState<Operacion>('listo');
  const [guardadoEn, setGuardadoEn] = useState<string | null>(null);
  const [pestana, setPestana] = useState<PestanaContexto>('cliente');
  const [claveSel, setClaveSel] = useState<string | null>(null);
  /** Sube al confirmar: el panel Pedidos enfoca el resultado de ESTA nota. */
  const [enfocarResultado, setEnfocarResultado] = useState(0);
  const grilla = useRef<GrillaLineasRef>(null);
  const botonGrabar = useRef<HTMLButtonElement>(null);
  const botonConfirmar = useRef<HTMLButtonElement>(null);
  const contenedorPago = useRef<HTMLDivElement>(null);
  /** El vuelto que se le da al cliente (efectivo «paga con»), para mostrarlo al terminar. */
  const [vueltoMeson, setVueltoMeson] = useState(0);
  const [textoAgregar, setTextoAgregar] = useState('');
  /** Línea recién agregada que espera el foco en su cantidad (se aplica tras pintarla). */
  const [enfocarCantidad, setEnfocarCantidad] = useState<string | null>(null);
  const intentos = useIntentos();

  const notaId = b.notaId ?? notaIdInicial ?? null;
  // Nota nueva: el número que vendría (como Random al abrir). Se asigna de verdad al guardar.
  const proximo = useProximoNumeroNota(empresaId, activa && !notaId);
  /** Firma EN LÍNEA: el vendedor espera la autorización con el diálogo abierto (consulta cada 3 s). */
  const [esperandoFirma, setEsperandoFirma] = useState(false);
  /** Cómo terminó la firma que se esperaba (se avisa aunque el vendedor haya seguido trabajando). */
  const [firma, setFirma] = useState<null | { resultado: string; motivo: string | null }>(null);
  const nota = useNota(empresaId, notaId, esperandoFirma);
  const actor = useNombreActor(nota.data?.nombres);
  const ficha = useFichaCliente(empresaId, b.cliente?.id ?? clienteInicialId ?? null);
  // Mesón · fase 1: el estado de pago viaja con la nota (recibos de caja).
  const pago = usePagoNota(empresaId, b.notaId ?? null, puede('caja.recibo.ver'));
  const relacion = ficha.data?.empresas.find((e) => e.empresa_id === empresaId) ?? null;
  /**
   * Lo que la ficha del cliente no tiene y la factura exige (R9.1: RUT, giro, dirección tributaria
   * con su comuna). 31-10 (NV-593): la factura se rechazaba DESPUÉS de cobrar y la venta quedaba
   * pagada sin documento; ahora la caja lo dice antes de cobrar.
   */
  const faltaParaFactura = useMemo(() => {
    const e = ficha.data?.entidad;
    if (!e) return [];
    const tributaria = ficha.data!.direcciones.find((d) => d.activa && d.es_tributaria);
    return [
      ...(!e.rut || e.rut === '66666666' ? ['RUT'] : []),
      ...(!e.giro?.trim() ? ['giro'] : []),
      ...(!tributaria
        ? ['dirección tributaria']
        : !tributaria.comuna_codigo
          ? ['comuna de la dirección tributaria']
          : []),
    ];
  }, [ficha.data]);
  /**
   * Rodrigo (2026-10-27): la nota confirmada que llega al punto de venta sin nada emitido (p. ej.
   * tomada «por retirar») no trae su documento: la caja elige boleta, factura o vale al emitir.
   */
  const notaPorEmitir =
    meson &&
    !!nota.data &&
    (nota.data.estado === 'CONFIRMADA' || nota.data.estado === 'CERRADA_CORTA') &&
    nota.data.documentada === false;
  const [docElegido, setDocElegido] = useState<DocumentoPos | null>(null);
  useEffect(() => setDocElegido(null), [notaId]);
  // Punto de venta: el documento tributario elegido y el folio que llevaría.
  // Sin elección en la venta: el documento de la ficha del cliente; si no tiene, boleta. La venta
  // hecha en el punto de venta ya eligió el suyo al grabarse (se respeta al reintentar la emisión).
  const docMeson: DocumentoPos = notaPorEmitir
    ? (docElegido ??
      (nota.data!.cabecera.canal === 'MESON'
        ? nota.data!.cabecera.documentoSolicitado
        : relacion?.documento_por_defecto === 'FACTURA'
          ? 'FACTURA'
          : 'BOLETA'))
    : b.documento === 'VALE'
      ? 'VALE'
      : (b.documento || b.cliente?.documentoPorDefecto) === 'FACTURA'
        ? 'FACTURA'
        : 'BOLETA';
  // Vale de venta (0109): con permiso, tercera opción del punto de venta, con su motivo.
  const puedeVale = puede('ventas.vale.emitir');
  const [motivoVale, setMotivoVale] = useState<MotivoVale>('VENTA');
  const [imprimirVale, setImprimirVale] = useState<string | null>(null);
  // Documento directo: su tipo SII fijo (factura 33, boleta 39, guía 52).
  const tipoDteFolio = meson
    ? docMeson === 'BOLETA'
      ? 39
      : 33
    : directo === 'BOLETA'
      ? 39
      : directo === 'GUIA'
        ? 52
        : 33;
  const folio = useProximoFolio(
    empresaId,
    tipoDteFolio,
    (meson || !!directo) && activa && docMeson !== 'VALE',
  );
  // 2A.1 §11: lo comercial lo decide el backend (valores por defecto, lo permitido y los permisos
  // efectivos del actor). La interfaz solo habilita lo que aquí viene permitido.
  const contexto = useContextoComercial(
    empresaId,
    b.cliente?.id ?? clienteInicialId ?? null,
    b.canal,
  );
  const k = contexto.data;
  const listaId =
    b.listaPrecioId ||
    nota.data?.cabecera.listaPrecioId ||
    k?.listaPrecio.porDefecto ||
    relacion?.lista_precio_id ||
    null;
  const stock = useDisponibilidad(empresaId, { ubicacionId: b.ubicacionId || null });
  const vendedores = useVendedores(empresaId);
  const listas = useListasPrecio(empresaId);

  // Bodega por defecto: la primera BODEGA de la empresa (GAP 20: no hay bodega por usuario o canal).
  useEffect(() => {
    if (!b.ubicacionId && !notaIdInicial && deVenta.length) {
      const bodega =
        deVenta.find((u) => u.id === ubicacionInicialId) ??
        deVenta.find((u) => u.tipo === 'BODEGA') ??
        deVenta[0]!;
      despachar({ tipo: 'cargar', borrador: { ...b, ubicacionId: bodega.id } });
    }
  }, [deVenta, b, notaIdInicial, ubicacionInicialId]);

  // Nota nueva desde la consulta de un cliente: el cliente ya viene elegido.
  useEffect(() => {
    if (clienteInicialId && !b.cliente && !notaIdInicial && ficha.data) {
      const e = ficha.data.entidad;
      despachar({
        tipo: 'cliente',
        cliente: {
          id: e.id,
          rut: e.rut ? `${e.rut}-${e.dv ?? ''}` : null,
          razonSocial: e.razon_social,
          condicionPago: relacion?.condicion_pago ?? null,
          bloqueado: !!relacion?.bloqueado,
          estado: e.estado,
        },
      });
    }
  }, [clienteInicialId, b.cliente, notaIdInicial, ficha.data, relacion]);

  // Dirección de despacho por defecto al elegir el cliente (GAP 9: el backend no elige una).
  useEffect(() => {
    if (ficha.data && b.cliente && !b.direccionEntregaId && !b.notaId && !notaIdInicial) {
      const d = direccionPorDefecto(ficha.data.direcciones);
      if (d) despachar({ tipo: 'cabecera', campos: { direccionEntregaId: d.id } });
    }
  }, [ficha.data, b.cliente, b.direccionEntregaId, b.notaId, notaIdInicial]);

  // Una nota existente se carga UNA vez como borrador (después, lo local manda hasta guardar).
  const cargada = useRef(false);
  useEffect(() => {
    if (nota.data && !cargada.current && !b.notaId) {
      cargada.current = true;
      despachar({ tipo: 'cargar', borrador: borradorDesdeNota(nota.data) });
      if (nota.data.estado !== 'BORRADOR') setPestana('pedidos');
    }
  }, [nota.data, b.notaId]);

  // Tras una transición (rechazar, aprobar…) la versión del backend avanza: el borrador la adopta.
  useEffect(() => {
    const d = nota.data;
    if (d && b.notaId === d.notaId && !b.sucio && b.version !== d.version)
      despachar({ tipo: 'guardado', notaId: d.notaId, version: d.version });
  }, [nota.data, b.notaId, b.sucio, b.version]);

  useEffect(() => alEnsuciar(b.sucio), [b.sucio, alEnsuciar]);

  // La firma llegó (aprobada → CONFIRMADA; rechazada → BORRADOR): se refresca todo lo de la nota y se
  // avisa, sin que el vendedor tenga que recargar.
  const estadoPrevio = useRef<string | null>(null);
  useEffect(() => {
    const d = nota.data;
    if (!d) return;
    const antes = estadoPrevio.current;
    estadoPrevio.current = d.estado;
    if (antes === 'EN_APROBACION' && d.estado !== 'EN_APROBACION') {
      const ultima = [...d.solicitudes]
        .filter((x) => x.estado !== 'PENDIENTE')
        .sort((x, y) => (x.resuelta_en ?? '').localeCompare(y.resuelta_en ?? ''))
        .at(-1);
      setFirma({
        resultado: ultima?.estado ?? (d.estado === 'BORRADOR' ? 'RECHAZADA' : 'APROBADA'),
        motivo: ultima?.motivo_resolucion ?? null,
      });
      void invalidar(d.notaId);
      if (d.estado !== 'BORRADOR') setPestana('pedidos');
    }
  }, [nota.data]);

  useEffect(() => {
    if (enfocarCantidad && b.lineas.some((l) => l.clave === enfocarCantidad)) {
      grilla.current?.enfocarCantidad(enfocarCantidad);
      setEnfocarCantidad(null);
    }
  }, [enfocarCantidad, b.lineas]);

  const n: Nota | undefined = nota.data;
  ultimaNota.current = { notaId, version: n?.version ?? null, estado: n?.estado ?? null };
  const numeroCargado = nota.data?.numeroVisible;
  useEffect(() => {
    if (meson && !mesonProp && b.notaId && numeroCargado)
      alTitular(`Punto de venta · ${numeroCargado}`, `nota:${b.notaId}`, {
        notaId: b.notaId,
        modo: 'meson',
      });
  }, [meson, mesonProp, b.notaId, numeroCargado]);
  const estado = n?.estado ?? 'BORRADOR';
  // Lo que manda es el documento tributario (2026-10-26): la factura o boleta emitida de la nota se
  // muestra e imprime por su folio; la NV queda como registro interno.
  const docsNota = useDocumentosEmitidos(
    empresaId,
    { notaId: notaId ?? undefined },
    !!notaId && estado !== 'BORRADOR',
  );
  const documentosVenta = (docsNota.data?.items ?? []).filter(
    (d) => [33, 34, 39, 41].includes(d.tipoDte) && !d.estado.startsWith('ANULADO'),
  );
  const docVenta = documentosVenta[0] ?? null;
  const ventaDirectaVista = meson || !!b.ventaDirecta || !!n?.ventaDirecta;
  // El motor asigna el folio en segundo plano: mientras falte, se vuelve a preguntar.
  const faltaFolio = documentosVenta.some((d) => !d.folio && d.estado === 'SOLICITADO');
  const refrescarDocs = docsNota.refetch;
  useEffect(() => {
    if (!faltaFolio) return;
    const t = setInterval(() => void refrescarDocs(), 4000);
    return () => clearInterval(t);
  }, [faltaFolio, refrescarDocs]);
  const [errorDte, setErrorDte] = useState<unknown>(null);
  // La venta directa se nombra por su documento: la pestaña dice «Boleta N° 4343», no la NV.
  const titularRef = useRef(alTitular);
  titularRef.current = alTitular;
  const folioVenta = ventaDirectaVista ? (docVenta?.folio ?? null) : null;
  const tipoVenta = docVenta?.tipoDte ?? null;
  useEffect(() => {
    if (!notaId || !folioVenta) return;
    titularRef.current(tituloDte(tipoVenta, folioVenta), `nota:${notaId}`, {
      notaId,
      ...(meson ? { modo: 'meson' } : {}),
    });
  }, [notaId, folioVenta, tipoVenta, meson]);
  /**
   * Modificar una nota CONFIRMADA (Rodrigo, 2026-10-25: «si llamo una nota y la quiero modificar,
   * debería poder cambiarla y guardar, y volver a llamarla»): queda editable y al guardar es una
   * revisión nueva (precio, crédito y firmas se vuelven a evaluar; mientras no haya salido nada).
   */
  const [modificando, setModificando] = useState(false);
  const enModificacion = modificando && estado === 'CONFIRMADA' && !meson;
  const editable =
    enModificacion ||
    (estado === 'BORRADOR' && (notaId ? puede('ventas.nota.editar') : puede('ventas.nota.crear')));
  const soloLectura = estado !== 'BORRADOR' && !enModificacion;
  // Entrega: en borrador va con lo que se guarda; ya enviada o confirmada se cambia sola (mientras
  // no haya salido nada: el backend lo controla).
  const entrega: EntregaNota =
    (editable ? b.entrega : n?.entrega) || n?.entrega || (meson ? 'RETIRO' : 'DESPACHO');
  const fechaEntrega = (editable ? b.fechaEntrega : n?.fechaEntrega) || '';
  const cambiarEntrega = useMutation({
    mutationFn: (e: { entrega?: EntregaNota; fechaEntrega?: string | null }) =>
      api.post(
        `/ventas/notas/${notaId}/entrega`,
        { version: n!.version, ...e },
        { idempotencia: intentos.clave('entrega', { notaId, ...e }) },
      ),
    onSuccess: async () => {
      intentos.cerrar();
      if (notaId) await invalidar(notaId);
    },
    onError: (e) => {
      intentos.cerrar(e);
      setError(e);
    },
  });
  const entregaCambiable =
    editable ||
    (!!n &&
      ['EN_APROBACION', 'CONFIRMADA'].includes(estado) &&
      puede('ventas.nota.editar') &&
      !cambiarEntrega.isPending);
  const elegirEntrega = (e: EntregaNota) => {
    if (editable) despachar({ tipo: 'cabecera', campos: { entrega: e } });
    else cambiarEntrega.mutate({ entrega: e });
  };
  const elegirFechaEntrega = (f: string) => {
    if (editable) despachar({ tipo: 'cabecera', campos: { fechaEntrega: f } });
    else cambiarEntrega.mutate({ fechaEntrega: f || null });
  };
  const lineasVista = soloLectura && n ? borradorDesdeNota(n).lineas : b.lineas;

  // ---------------------------------------------------------------- previsualización (sin guardar)
  const cuerpoPrev = useMemo(() => {
    if (!editable || !puede('ventas.nota.crear')) return null;
    const c = cuerpoPrevisualizar(b, empresaId);
    // 0115: el documento directo sale sin pago: la vista previa ya anuncia la firma SIN_PAGO.
    return c && !meson && directo && directo !== 'GUIA' ? { ...c, sinPago: true } : c;
  }, [b, editable, empresaId, puede, meson, directo]);
  const prev = usePrevisualizacion(empresaId, cuerpoPrev);
  const vista: Previsualizacion | undefined = editable ? prev.datos : undefined;
  /**
   * Lo que impide confirmar: la forma del borrador y el stock (sin stock negativo: Rodrigo, 2026-10-23;
   * no se confirma ni se pide firma). Solo guardar el borrador no exige stock (2026-10-29: pedidos
   * programados para el día siguiente; se guarda, no se procesa). El backend lo exige al confirmar.
   */
  const problemasAlConfirmar = (): ProblemaBorrador[] => [
    ...problemasDe(b),
    ...(vista?.disponibilidad ?? [])
      .filter((d) => !d.alcanza)
      .map((d) => {
        const l = b.lineas.find((x) => x.productoId === d.productoId);
        return {
          campo: 'lineas',
          mensaje: `Stock insuficiente: ${l?.sku ?? ''} ${l?.nombre ?? ''} pide ${Number(d.requerido).toLocaleString('es-CL')} y hay ${Number(d.disponible).toLocaleString('es-CL')}. Corrige la cantidad`,
        };
      }),
  ];
  const desactualizado = editable && prev.estado !== 'actualizado';

  const invalidar = async (id: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: claves.nota(empresaId, id) }),
      qc.invalidateQueries({ queryKey: ['emp', empresaId, 'disponibilidad'] }),
      qc.invalidateQueries({ queryKey: ['emp', empresaId, 'notas'] }),
      qc.invalidateQueries({ queryKey: claves.cumplimiento(empresaId, id) }),
      qc.invalidateQueries({ queryKey: clavesDocumentos.todos(empresaId) }),
    ]);
  };

  // ---------------------------------------------------------------- eliminar y guardar (como Random)
  /**
   * «Eliminar y guardar» de Random (Rodrigo, 2026-10-26): la nota se elimina (queda ANULADA, auditada)
   * y queda una copia en BORRADOR para usarla de nuevo. Primero se crea la copia y después se anula:
   * si la anulación no procede (DTE vigente, ODD, mercadería afuera), la copia se descarta y la nota
   * sigue como estaba.
   */
  const intentosEliminar = useIntentos();
  const eliminarYGuardar = useMutation({
    mutationFn: async (motivo: string) => {
      if (!n || !notaId) throw new Error('No hay nota para eliminar');
      const cuerpo = cuerpoCrear(
        { ...borradorDesdeNota(n), notaId: null, fechaEntrega: '' },
        empresaId,
      );
      const copia = await api.post<ResumenNota>('/ventas/notas', cuerpo, {
        idempotencia: intentosEliminar.clave('copia-al-eliminar', { notaId, cuerpo }),
      });
      try {
        await api.post(
          `/ventas/notas/${notaId}/anular`,
          { version: n.version, motivo },
          { idempotencia: intentosEliminar.clave('eliminar', { notaId, motivo }) },
        );
      } catch (e) {
        await api
          .post(
            `/ventas/notas/${copia.notaId}/descartar`,
            { version: copia.version },
            { idempotencia: nuevaClaveIdempotencia('descartar-copia') },
          )
          .catch(() => undefined);
        throw e;
      }
      return copia;
    },
    onSuccess: async (copia) => {
      intentosEliminar.cerrar();
      setDialogo(null);
      if (notaId) await invalidar(notaId);
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'documentos', 'guardados'] });
      // La copia se abre en su pestaña, lista para usarla.
      alAbrirNota?.(copia.notaId, copia.numeroVisible);
    },
    onError: (e) => intentosEliminar.cerrar(e),
  });

  // ---------------------------------------------------------------- guardar (crea o edita la revisión)
  const guardar = useMutation({
    mutationFn: async () => {
      if (!b.notaId) {
        const cuerpo = cuerpoCrear(b, empresaId);
        return api.post<ResumenNota>('/ventas/notas', cuerpo, {
          idempotencia: intentos.clave('crear-nota', cuerpo),
        });
      }
      if (enModificacion) {
        // La revisión no cambia la entrega (va por su propio camino) y queda con su motivo fijo.
        const {
          entrega: _e,
          fechaEntrega: _f,
          ...resto
        } = cuerpoEditar({
          ...b,
          version: n!.version,
        });
        void _e;
        void _f;
        const cuerpo = { ...resto, motivo: 'Modificación de la nota' };
        return api.post<ResumenNota>(`/ventas/notas/${b.notaId}/revisar`, cuerpo, {
          idempotencia: intentos.clave('revisar-nota', { id: b.notaId, cuerpo }),
        });
      }
      const cuerpo = cuerpoEditar(b);
      return api.patch<ResumenNota>(`/ventas/notas/${b.notaId}`, cuerpo, {
        idempotencia: intentos.clave('editar-nota', { id: b.notaId, cuerpo }),
      });
    },
    onMutate: () => {
      setOp('guardando');
      setError(null);
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      despachar({ tipo: 'guardado', notaId: r.notaId, version: r.version });
      alTitular(
        meson
          ? `Punto de venta · ${r.numeroVisible}`
          : directo && directo !== 'GUIA'
            ? `${NOMBRE_DIRECTO[directo]} · ${r.numeroVisible.replace(/^NV-/, '')}`
            : r.numeroVisible,
        `nota:${r.notaId}`,
        {
          notaId: r.notaId,
          ...(meson ? { modo: 'meson' } : {}),
        },
      );
      setGuardadoEn(new Date().toLocaleTimeString('es-CL'));
      if (enModificacion) setModificando(false);
      await invalidar(r.notaId);
      setOp('listo');
    },
    onError: (e) => {
      intentos.cerrar(e);
      setError(e);
      setOp('error');
    },
  });

  /**
   * «Guardar borrador» (pedido de Rodrigo, 2026-10-09): guarda y deja la pantalla limpia para la
   * siguiente nota; la guardada se avisa arriba y se reabre con un clic.
   */
  const [avisoGuardada, setAvisoGuardada] = useState(guardadaRecien ?? null);
  const guardarYLimpiar = async () => {
    if (!editable || guardar.isPending) return;
    // Guardar sin confirmar: solo la forma (el stock que falta no impide guardar el borrador).
    const p = problemasDe(b);
    setProblemas(p);
    if (p.length) return;
    // Tomada de los guardados y sin cambios: no hay nada que grabar, solo vuelve a «Guardados».
    if (!b.sucio && b.notaId && n) {
      tomada.current.reguardada = true;
      if (alNuevaNota) alNuevaNota({ notaId: n.notaId, numero: n.numeroVisible });
      return;
    }
    try {
      const r = await guardar.mutateAsync();
      tomada.current.reguardada = true;
      if (alNuevaNota) alNuevaNota({ notaId: r.notaId, numero: r.numeroVisible });
    } catch {
      /* el error ya se muestra (onError) y la nota sigue en pantalla */
    }
  };

  /** Antes de confirmar: valida la FORMA y el stock, y guarda si hay cambios (nota o revisión nueva). */
  const guardarSiHaceFalta = async (forzar = false): Promise<boolean> => {
    if (!editable || guardar.isPending) return false;
    const p = problemasAlConfirmar();
    setProblemas(p);
    if (p.length) return false;
    if (!forzar && !b.sucio && b.notaId) return true;
    try {
      await guardar.mutateAsync();
      return true;
    } catch {
      return false;
    }
  };

  /**
   * F8: recalcular AHORA (sin guardar). Si falta algo de forma, dice qué. El foco no se mueve.
   * Si hay líneas digitadas con otra lista que la de la nota, las pasa a la lista de la nota y
   * las re-precia (el cambio del borrador dispara la previa sola).
   */
  const recalcular = () => {
    if (!editable) return;
    const p = problemasDe(b);
    setProblemas(p);
    if (p.length) return;
    if (lineasOtraLista > 0) despachar({ tipo: 'recalcularLista' });
    else void prev.recalcularAhora();
  };

  // ---------------------------------------------------------------- transiciones
  const transicion = useMutation({
    mutationFn: async (v: { accion: string; motivo?: string; enLinea?: boolean }) => {
      const version = nota.data?.version ?? b.version!;
      // 0115: el documento directo, o el punto de venta sin pago, se confirma declarándolo.
      const sinPago =
        v.accion === 'confirmar' &&
        (sinPagoMeson.current || (!meson && !!directo && directo !== 'GUIA'));
      const ruta =
        v.accion === 'reservar'
          ? `/ventas/notas/${notaId}/reservar`
          : `/ventas/notas/${notaId}/${v.accion}`;
      const cuerpo =
        v.accion === 'reservar'
          ? { motivo: v.motivo }
          : {
              version,
              ...(v.motivo ? { motivo: v.motivo } : {}),
              ...(sinPago ? { sinPago: true } : {}),
            };
      return api.post<ResultadoConfirmar>(ruta, cuerpo, {
        idempotencia: intentos.clave(v.accion, { notaId, cuerpo }),
      });
    },
    onMutate: () => {
      setOp('procesando');
      setError(null);
    },
    onSuccess: async (r, v) => {
      intentos.cerrar();
      // Descartado: lo digitado se pierde y la pestaña queda limpia para otra nota.
      if (v.accion === 'descartar') {
        setDialogo(null);
        await Promise.all([
          notaId ? invalidar(notaId) : null,
          qc.invalidateQueries({ queryKey: ['emp', empresaId, 'documentos', 'guardados'] }),
        ]);
        setOp('listo');
        if (alNuevaNota) {
          alNuevaNota();
          return;
        }
      }
      let siguiente: Dialogo = null;
      if (v.accion === 'confirmar') {
        setFirma(null);
        // En línea: queda esperando la firma con el diálogo abierto; si no, solo el aviso.
        if (r.estado === 'EN_APROBACION' && v.enLinea) setEsperandoFirma(true);
        else if (meson && r.estado === 'CONFIRMADA' && cobrarTrasConfirmar) {
          // Punto de venta: confirmada → al cobro de inmediato (y después, la boleta).
          setCobrarTrasConfirmar(false);
          setReciboMeson(null);
          if (sinPagoMeson.current) {
            // Sin pago (con permiso o ya firmada): sale el documento sin recibo.
            sinPagoMeson.current = false;
            emitir.mutate(null);
          } else if (pagoEnPantalla.current) {
            pagoEnPantalla.current = false;
            cobroMeson.mutate();
          } else siguiente = { tipo: 'cobrar' };
        } else {
          setResultado(r);
          // La nota terminó: se ofrece partir la siguiente (o cobrar, en el mesón).
          if (alNuevaNota) setTerminada(r);
        }
      }
      setDialogo(siguiente);
      if (notaId) await invalidar(notaId);
      setOp('listo');
      // Después de confirmar, la reserva y el seguimiento se ven de inmediato en «Pedidos».
      if (v.accion === 'confirmar') {
        setPestana('pedidos');
        // Con el resumen «terminada» abierto, el foco es suyo (Enter = nueva nota); el resultado
        // se enfoca al elegir «Ver la nota».
        if (!alNuevaNota || (r.estado === 'EN_APROBACION' && v.enLinea))
          setEnfocarResultado((x) => x + 1);
        if (meson && r.estado !== 'CONFIRMADA') setCobrarTrasConfirmar(false);
      }
    },
    onError: (e) => {
      intentos.cerrar(e);
      setError(e);
      setOp('error');
    },
  });

  // ---------------------------------------------------------------- punto de venta (mesón)
  /**
   * F10 «Grabar y pagar» en el mesón (pedido de Rodrigo, 2026-10-22: el punto de venta es venta
   * directa, no una nota de venta): guarda, confirma SIN el resumen de la nota y abre el cobro; al
   * pagar sale la boleta/factura. Solo si la venta pide una firma se muestra el resumen (autorización
   * en línea).
   */
  const cobrarYEmitir = async (sinPago = false) => {
    if (estado !== 'BORRADOR' || !puede('ventas.nota.confirmar') || !b.lineas.length) return;
    if (meson && docMeson === 'FACTURA' && faltaParaFactura.length) {
      refrescarReceptor.current = true;
      setAvisoCaja('factura');
      return;
    }
    sinPagoMeson.current = sinPago;
    // Punto de venta: se graba con los pagos cuadrados (venta = pagos), como en Random. Sin turno
    // de caja se ofrece abrirla; sin pagos, emitir sin pago (con permiso o con firma).
    if (meson && puede('caja.recibo.registrar') && !sinPago) {
      if (desactualizado) return;
      if (!pagoMeson.listo) {
        if (sinTurnoMeson && puede('caja.turno.operar')) setAvisoCaja('abrir');
        else if (pagoMeson.pagos === 0) setAvisoCaja('sin-pago');
        return;
      }
      pagoEnPantalla.current = true;
      setVueltoMeson(pagoMeson.vuelto);
    }
    setCobrarTrasConfirmar(true);
    // La nota guarda los datos del cliente al grabarse: completada la ficha, se graba de nuevo.
    if (!(await guardarSiHaceFalta(refrescarReceptor.current && !!b.notaId))) {
      setCobrarTrasConfirmar(false);
      return;
    }
    refrescarReceptor.current = false;
    // La nota recién grabada llega en el render siguiente: ahí se confirma (ver el efecto).
    setConfirmarMeson(true);
  };
  const [confirmarMeson, setConfirmarMeson] = useState(false);
  useEffect(() => {
    if (!confirmarMeson || !notaId || !n || n.estado !== 'BORRADOR' || transicion.isPending) return;
    setConfirmarMeson(false);
    if (requeridosMeson.current.length) setDialogo({ tipo: 'confirmar' });
    else
      transicion.mutate({
        accion: 'confirmar',
        // Sin pago y sin el permiso: la firma SIN_PAGO se pide en línea (el cajero espera).
        ...(sinPagoMeson.current && !puede('ventas.documento.sin_pago') ? { enLinea: true } : {}),
      });
  }, [confirmarMeson, notaId, n, transicion]);
  /** La boleta/factura de la venta de mesón (en VALIDACIÓN la emisión está bloqueada: se avisa). */
  const emitir = useMutation({
    // Sin recibo: la nota está pagada de antes, se confirmó «sin pago» o tiene la autorización en
    // línea firmada. La caja nunca emite sin pago por su cuenta: el backend lo vuelve a exigir
    // (puntoDeVenta) aunque la nota sea a crédito (Rodrigo, 2026-10-27).
    mutationFn: (recibo: ReciboRegistrado | null) => {
      const soloEfectivo = (recibo?.medios ?? []).every((m) => m.medio === 'EFECTIVO');
      // El documento que eligió la caja (si la nota aún no tiene nada emitido) o el de la nota.
      const doc = notaPorEmitir ? docMeson : nota.data?.cabecera.documentoSolicitado;
      const cuerpo = {
        puntoDeVenta: true,
        ...(notaPorEmitir ? { documento: docMeson } : {}),
        ...(doc === 'BOLETA'
          ? { medioPago: soloEfectivo ? 1 : 2 }
          : doc === 'VALE'
            ? { valeMotivo: motivoVale }
            : {}),
      };
      setDocVentaMeson(doc ?? null);
      setVentaMeson({ recibo, documento: 'EMITIENDO' });
      return api.post(`/ventas/notas/${notaId}/documentar`, cuerpo, {
        idempotencia: intentos.clave('documentar-meson', { notaId, cuerpo }),
      });
    },
    onSuccess: async (r, recibo) => {
      intentos.cerrar();
      const x = r as {
        dteDocumentoId?: string | null;
        valeId?: string;
        valeNumero?: string;
      } | null;
      const dteId = x?.dteDocumentoId ?? null;
      setVentaMeson({
        recibo,
        documento: 'SOLICITADO',
        ...(dteId ? { dteId } : {}),
        ...(x?.valeId ? { valeId: x.valeId, valeNumero: x.valeNumero ?? '' } : {}),
      });
      // El vale sale impreso en el acto (no espera emisión: es interno).
      if (x?.valeId) setImprimirVale(x.valeId);
      if (notaId) await invalidar(notaId);
    },
    onError: (e, recibo) => {
      intentos.cerrar(e);
      const codigo = e instanceof ErrorApi ? e.codigo : null;
      setVentaMeson({
        recibo,
        documento: codigo === 'BLOQUEADO_POR_MODO_OPERACION' ? 'BLOQUEADO_VALIDACION' : 'ERROR',
        error: e,
      });
    },
  });

  /** El recibo de caja con los medios de la pantalla; pagada la venta, se pide la boleta/factura. */
  const cobroMeson = useMutation({
    mutationFn: () => {
      const cuerpo = {
        empresaId,
        clienteEntidadId: b.cliente!.id,
        notaId: notaId!,
        medios: filasPago.map(medioDe),
      };
      return api.post<ReciboRegistrado>('/caja/recibos', cuerpo, {
        idempotencia: intentos.clave('cobrar-meson', cuerpo),
      });
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      if (notaId) await invalidar(notaId);
      if (r.estadoPago === 'PAGADA') emitir.mutate(r);
      else setVentaMeson({ recibo: r, documento: 'ERROR' });
    },
    onError: (e) => {
      intentos.cerrar(e);
      setError(e);
    },
  });
  /**
   * Punto de venta con firma en línea (Rodrigo, 2026-10-29: la NV-000348 quedó confirmada sin cobro
   * ni boleta y en la pantalla parecía una nota de venta). Autorizada mientras la cajera espera, la
   * venta sigue sola igual que sin firma: cobra los pagos de la pantalla y emite la boleta/factura.
   */
  useEffect(() => {
    if (!meson || !esperandoFirma || firma?.resultado !== 'APROBADA') return;
    if (n?.estado !== 'CONFIRMADA') return;
    setEsperandoFirma(false);
    setFirma(null);
    setReciboMeson(null);
    if (sinPagoMeson.current) {
      sinPagoMeson.current = false;
      emitir.mutate(null);
    } else if (pagoEnPantalla.current) {
      pagoEnPantalla.current = false;
      cobroMeson.mutate();
    } else setDialogo({ tipo: 'cobrar' });
  }, [meson, esperandoFirma, firma, n?.estado]);
  /** Imprime la boleta/factura de la venta (original y cedible si corresponde). */
  const imprimirDte = async (dteId: string) => {
    setErrorImpresion(null);
    try {
      const pdf = await pedirArchivo('GET', `/dte/documentos/${dteId}/pdf`, {
        query: { ejemplar: 'dos' },
      });
      imprimirPdf(pdf);
    } catch (e) {
      setErrorImpresion(e);
    }
  };
  /**
   * Punto de venta: la boleta/factura sale impresa sola al terminar la venta (Rodrigo, 2026-10-24).
   * El motor la firma en segundos; mientras no está, el PDF no existe: se reintenta un rato.
   */
  const impresaSola = useRef<string | null>(null);
  const dteMeson = ventaMeson?.documento === 'SOLICITADO' ? (ventaMeson.dteId ?? null) : null;
  useEffect(() => {
    if (!dteMeson || impresaSola.current === dteMeson) return;
    impresaSola.current = dteMeson;
    void (async () => {
      setErrorImpresion(null);
      for (let intento = 1; ; intento++) {
        try {
          const pdf = await pedirArchivo('GET', `/dte/documentos/${dteMeson}/pdf`, {
            query: { ejemplar: 'dos' },
          });
          imprimirPdf(pdf);
          return;
        } catch (e) {
          if (intento >= 20) {
            setErrorImpresion(e);
            return;
          }
          await new Promise((ok) => window.setTimeout(ok, 1500));
        }
      }
    })();
  }, [dteMeson]);

  /**
   * Factura o boleta directa (2026-10-23): al emitirla se dice si el cliente se lleva la mercadería
   * ahora (queda despachada sola al enviarse al SII) o si queda pendiente de despacho (se paga y se
   * despacha después, con su guía).
   */
  const [emisionDirecta, setEmisionDirecta] = useState<EmisionDirecta | null>(null);
  const emitirDirecto = useMutation({
    mutationFn: (inmediato: boolean) => {
      const id = terminada?.notaId ?? notaId;
      setEmisionDirecta({ estado: 'EMITIENDO', inmediato });
      return api.post(
        `/ventas/notas/${id}/documentar`,
        { despachoInmediato: inmediato },
        { idempotencia: intentos.clave('documentar-directo', { notaId: id }) },
      );
    },
    onSuccess: async (r, inmediato) => {
      intentos.cerrar();
      const dteId = (r as { dteDocumentoId?: string } | null)?.dteDocumentoId;
      setEmisionDirecta({ estado: 'SOLICITADO', inmediato, ...(dteId ? { dteId } : {}) });
      if (notaId) await invalidar(notaId);
    },
    onError: (e, inmediato) => {
      intentos.cerrar(e);
      setEmisionDirecta({ estado: 'ERROR', inmediato, error: e });
    },
  });

  const abrirConfirmacion = async () => {
    if (estado !== 'BORRADOR' || !puede('ventas.nota.confirmar')) return;
    if (await guardarSiHaceFalta()) setDialogo({ tipo: 'confirmar' });
  };

  const elegirCliente = (c: ClienteEncontrado) => {
    despachar({ tipo: 'cliente', cliente: clienteDesdeBusqueda(c) });
    despachar({
      tipo: 'cabecera',
      campos: {
        direccionEntregaId: '',
        condicionPago: '',
        plazoDias: '',
        vendedorId: '',
        cambioVendedor: null,
        listaPrecioId: '',
      },
    });
    setDialogo(null);
    setPestana('cliente');
    setTimeout(() => grilla.current?.enfocarAgregar(), 0);
  };

  const pedirQuitar = (clave: string) => {
    const l = b.lineas.find((x) => x.clave === clave);
    if (l) setDialogo({ tipo: 'quitar', clave, texto: `${l.sku} · ${l.nombre}` });
  };

  // Recorrer la grilla NO abre el panel Producto: solo se abre cuando se pide (clic, Enter, Alt+S).
  const seleccionar = (clave: string) => setClaveSel(clave);
  const verProducto = (clave: string) => {
    setClaveSel(clave);
    setPestana('producto');
  };

  /** Agrega una línea en UNIDAD BASE (la presentación elegida, si es otra, queda como equivalencia). */
  const agregarLinea = (
    producto: { id: string; sku: string; nombre: string },
    base: { id: string },
    equivalencia: { codigo: string; factor: string } | null,
  ) => {
    const clave = `n${b.secuencia}`;
    despachar({
      tipo: 'agregar',
      linea: {
        productoId: producto.id,
        sku: producto.sku,
        nombre: producto.nombre,
        presentacionId: base.id,
        cantidad: equivalencia ? String(Number(equivalencia.factor)) : '1',
        precioManual: null,
        descuento: null,
        equivalencia: equivalencia
          ? { codigo: equivalencia.codigo, cantidad: '1', factor: equivalencia.factor }
          : null,
      },
    });
    setTextoAgregar('');
    setClaveSel(clave);
    // Foco a la cantidad de la línea recién agregada, editando, cuando la línea ya está pintada.
    setEnfocarCantidad(clave);
  };

  /** Enter en el código: si es EXACTO y único se agrega directo; si no, el buscador (F3). */
  const ingresarCodigo = async (texto: string) => {
    const t = texto.trim();
    if (!t) {
      setDialogo({ tipo: 'producto', texto: '' });
      return;
    }
    const exacto = await resolverCodigo(empresaId, t).catch(() => null);
    if (exacto) agregarLinea(exacto.producto, exacto.base, null);
    else setDialogo({ tipo: 'producto', texto: t });
  };

  // Mesón: se cobra una nota GUARDADA y vigente, con saldo, si el usuario puede cobrar.
  const cobrable =
    !!n &&
    ['BORRADOR', 'EN_APROBACION', 'CONFIRMADA', 'CERRADA_CORTA'].includes(estado) &&
    puede('caja.recibo.registrar') &&
    !!pago.data &&
    Number(pago.data.saldo) > 0;

  // Punto de venta: al elegir al cliente se ofrecen sus notas confirmadas sin documento (retiro).
  const clientePorRetirar =
    meson &&
    !notaId &&
    b.cliente &&
    b.cliente.id !== consumidor.data &&
    b.cliente.rut !== '66666666'
      ? b.cliente.id
      : null;
  const porRetirar = useNotasPorRetirar(empresaId, alTomarNota ? clientePorRetirar : null);
  const notasPorRetirar = clientePorRetirar ? (porRetirar.data?.items ?? []) : [];
  const [porRetirarVisto, setPorRetirarVisto] = useState<string | null>(null);
  const verPorRetirar =
    !!clientePorRetirar &&
    notasPorRetirar.length > 0 &&
    porRetirarVisto !== clientePorRetirar &&
    !b.lineas.length &&
    dialogo === null;
  const tomarNota = async (x: NotaListada) => {
    setPorRetirarVisto(clientePorRetirar);
    try {
      // El cliente vino a buscarla: si iba a despacho, pasa a retiro (el backend rechaza si va en una OD).
      if (x.entrega !== 'RETIRO')
        await api.post(
          `/ventas/notas/${x.id}/entrega`,
          { version: x.version, entrega: 'RETIRO' },
          { idempotencia: `entrega-${x.id}-${x.version}` },
        );
      alTomarNota?.(x.id, x.numeroVisible);
    } catch (e) {
      setError(e);
    }
  };

  /** Botón «Guardar borrador»: con cambios, nota nueva o tomada de los guardados (Rodrigo, 2026-10-26). */
  const guardableBorrador =
    editable &&
    (b.sucio ||
      !b.notaId ||
      (tomadaDeGuardados && estado === 'BORRADOR' && !tomada.current.reguardada));
  /**
   * «Limpiar» (pedido de Rodrigo, 2026-10-10): deja la pestaña en blanco para empezar de nuevo, sin
   * preguntar. Lo no guardado se pierde; una nota ya guardada sigue guardada (no se descarta).
   */
  const limpiable = !!alNuevaNota && (!!b.cliente || b.lineas.length > 0 || !!notaId);
  const limpiar = () => {
    soltarTomada();
    alNuevaNota?.();
  };

  /**
   * Punto de venta con una nota confirmada sin nada emitido (p. ej. tomada «por retirar»). Rodrigo,
   * 2026-10-27: la caja NUNCA emite sin pago por su cuenta, aunque el cliente tenga crédito o la nota
   * sea a crédito (contado, transferencia, crédito, cheque: da igual). Pagada (o confirmada «sin
   * pago» con su firma) se emite; con saldo se cobra (F10) o se pide la autorización en línea.
   */
  const emitibleMeson = notaPorEmitir && !!n && !ventaMeson && !emitir.isPending;
  const saldoMeson = pago.data ? Number(pago.data.saldo) : null;
  const sinPagoQ = useAutorizacionSinPago(
    empresaId,
    meson && notaPorEmitir ? notaId : null,
    esperandoSinPago,
  );
  const autorizacionSinPago = sinPagoQ.data?.autorizacion ?? null;
  const autorizadaSinPago =
    autorizacionSinPago?.estado === 'APROBADA' && autorizacionSinPago.revision === n?.revision;
  const emitiblePagada =
    emitibleMeson && (n!.sinPagoAutorizado === true || saldoMeson === 0 || autorizadaSinPago);
  /** Con saldo (o sin saber el saldo): emitir sin pago con el permiso o con la firma en línea. */
  const emitibleSinPago = emitibleMeson && !emitiblePagada;
  const pedirSinPago = useMutation({
    mutationFn: () =>
      api.post<{ estado: string }>(
        `/ventas/notas/${notaId}/sin-pago`,
        { documento: docMeson },
        { idempotencia: intentos.clave('sin-pago', { notaId, documento: docMeson }) },
      ),
    onSuccess: async (r) => {
      intentos.cerrar();
      await sinPagoQ.refetch();
      // Ya estaba firmada para este contenido: sale de inmediato.
      if (r.estado === 'APROBADA') emitir.mutate(null);
      else setEsperandoSinPago(true);
    },
    onError: (e) => {
      intentos.cerrar(e);
      setError(e);
    },
  });
  const retirarSinPago = useMutation({
    mutationFn: (a: { id: string; version: number }) =>
      api.post(
        `/ventas/sin-pago/${a.id}/retirar`,
        { version: a.version },
        { idempotencia: nuevaClaveIdempotencia('sin-pago-retirar') },
      ),
    onSuccess: async () => {
      await sinPagoQ.refetch();
    },
    onError: (e) => setError(e),
  });
  // Firmada mientras la cajera esperaba: el documento sale solo.
  const estadoSinPago = esperandoSinPago ? (autorizacionSinPago?.estado ?? null) : null;
  useEffect(() => {
    if (estadoSinPago !== 'APROBADA') return;
    setEsperandoSinPago(false);
    emitir.mutate(null);
  }, [estadoSinPago]);
  /** «Emitir sin pago»: con el permiso sale directo; sin él, se pide la firma en línea. */
  const emitirSinPago = () => {
    if (puede('ventas.documento.sin_pago')) emitir.mutate(null);
    else pedirSinPago.mutate();
  };

  /**
   * Factura o boleta directa (no el mesón) confirmada y sin documento. Rodrigo, 2026-11-02 (venta
   * 724, autorizada en línea): «Emitir» solo salía en el aviso de la nota recién confirmada sin
   * firma; después la pantalla ofrecía cobrar, imprimir la NV o ir a despacho, como si fuera una nota
   * de venta. Ahora «Emitir» queda a la vista hasta que sale el documento.
   */
  const directaPorEmitir =
    !meson &&
    !!n?.ventaDirecta &&
    (estado === 'CONFIRMADA' || estado === 'CERRADA_CORTA') &&
    n.documentada === false &&
    !enModificacion;
  const emitibleDirecta = directaPorEmitir && puede('dte.solicitar') && !sinEmision;
  const nombreDocDirecta = n?.cabecera.documentoSolicitado === 'BOLETA' ? 'boleta' : 'factura';
  /**
   * La venta directa que se retira se emite con su pago (0115): con saldo y sin el permiso para
   * emitir sin pago, F10 cobra primero y al cerrar el cobro se ofrece emitir.
   */
  const cobrarAntesDeEmitir =
    emitibleDirecta &&
    entrega === 'RETIRO' &&
    !n!.sinPagoAutorizado &&
    cobrable &&
    !puede('ventas.documento.sin_pago');
  const abrirEmitirDirecta = () => {
    // Un intento fallido se puede repetir; uno pedido no se vuelve a pedir.
    if (emisionDirecta?.estado === 'ERROR') setEmisionDirecta(null);
    setDialogo({ tipo: 'emitir-directa' });
  };
  // Autorizada en línea mientras se esperaba: la venta directa sigue sola a emitir su documento.
  useEffect(() => {
    if (meson || !esperandoFirma || firma?.resultado !== 'APROBADA') return;
    if (!n?.ventaDirecta || n.estado !== 'CONFIRMADA' || !puede('dte.solicitar') || sinEmision)
      return;
    setEsperandoFirma(false);
    setFirma(null);
    abrirEmitirDirecta();
  }, [meson, esperandoFirma, firma, n?.estado, n?.ventaDirecta]);

  useAtajos(activa && dialogo === null, {
    F2: () => editable && !b.notaId && setDialogo({ tipo: 'cliente' }),
    F3: () => editable && b.cliente && setDialogo({ tipo: 'producto', texto: '' }),
    F5: () => setPestana('cartola'),
    F6: () => setPestana('pedidos'),
    F7: () => setPestana('compras'),
    F8: () => recalcular(),
    F9: () => void (meson ? cobrarYEmitir() : abrirConfirmacion()),
    // Random: F4 = «Grabar documento de formalización».
    F4: () =>
      meson && estado === 'BORRADOR' ? void cobrarYEmitir() : grilla.current?.enfocarCantidad(),
    'Alt+G': () => editable && b.lineas.length > 0 && void guardarYLimpiar(),
    F10: () => {
      if (meson && estado === 'BORRADOR') void cobrarYEmitir();
      else if (cobroNotaMeson) cobrarNotaMeson();
      else if (emitiblePagada) emitir.mutate(null);
      else if (emitibleDirecta && !cobrarAntesDeEmitir) abrirEmitirDirecta();
      else if (cobrable) {
        setReciboMeson(null);
        setDialogo({ tipo: 'cobrar' });
      }
    },
    'Alt+C': () => setPestana('cliente'),
    'Alt+S': () => setPestana('producto'),
    'Alt+M': () => editable && setDialogo({ tipo: 'otros' }),
    'Alt+B': () => editable && setDialogo({ tipo: 'observacion' }),
    'Alt+F': () => b.cliente && alAbrirFicha?.(b.cliente.id, b.cliente.razonSocial),
    'Alt+L': () => limpiable && limpiar(),
    'Alt+T': () => {
      const siguiente: DocumentoPos =
        docMeson === 'BOLETA' ? 'FACTURA' : docMeson === 'FACTURA' && puedeVale ? 'VALE' : 'BOLETA';
      if (meson && emitibleMeson) setDocElegido(siguiente);
      else if (meson && editable) despachar({ tipo: 'cabecera', campos: { documento: siguiente } });
    },
    // La venta directa se despacha después de emitir su documento, no antes.
    'Alt+P': () =>
      alAbrirDespacho &&
      n &&
      estado === 'CONFIRMADA' &&
      !directaPorEmitir &&
      alAbrirDespacho(n.notaId, n.numeroVisible),
  });

  const pendientes = n?.reservas.pendiente && !esCero(n.reservas.pendiente);
  const solicitudPendiente = n?.solicitudes.find((s) => s.estado === 'PENDIENTE');
  const alertaCliente =
    b.cliente && (b.cliente.bloqueado || relacion?.bloqueado || b.cliente.estado !== 'activa');
  const calculo = editable ? ESTADO_CALCULO[prev.estado] : null;
  // Lo que se pinta como CALCULADO: la previsualización (editable) o lo guardado (solo lectura).
  const calculadas = editable ? (vista?.lineas ?? null) : (n?.lineas ?? null);
  const totales = editable ? vista?.totales : n?.totales;
  const cargos = editable ? vista?.cargos : n?.cargos;
  const totalVenta = meson && totales ? Math.round(Number(totales.bruto)) : 0;
  const turnoMeson = useTurnoActual(empresaId, meson && puede('caja.turno.operar'));
  const sinTurnoMeson = turnoMeson.isSuccess && turnoMeson.data === null;
  const pagoMeson = estadoPago(totalVenta, filasPago, sinTurnoMeson);
  /**
   * Rodrigo (2026-10-30): «todo documento por punto de venta debe permitir ingresarle el pago». La
   * nota confirmada que llega a la caja sin documento (tomada por retirar) lleva los mismos medios de
   * pago abajo, por su saldo, y F10 cobra y emite. Antes solo había «Cobrar» en un diálogo aparte y,
   * firmada la emisión sin pago, ni eso: la NV-000453 se terminó rehaciendo como otra venta.
   */
  const cobroNotaMeson =
    emitibleMeson && puede('caja.recibo.registrar') && saldoMeson !== null && saldoMeson > 0;
  const pagoNotaMeson = estadoPago(
    cobroNotaMeson ? (saldoMeson ?? 0) : 0,
    filasPago,
    sinTurnoMeson,
  );
  /** Los medios de pago a la vista: la venta que se digita o la nota por emitir con saldo. */
  const conPagoMeson = meson && puede('caja.recibo.registrar') && (editable || cobroNotaMeson);
  const botonCobrarNota = useRef<HTMLButtonElement>(null);
  /** F10 en la nota por emitir: con los pagos de la pantalla, recibo y documento en el acto. */
  const cobrarNotaMeson = () => {
    if (cobroMeson.isPending) return;
    if (docMeson === 'FACTURA' && faltaParaFactura.length) {
      setAvisoCaja('factura');
      return;
    }
    if (pagoNotaMeson.listo) {
      setReciboMeson(null);
      setVueltoMeson(pagoNotaMeson.vuelto);
      cobroMeson.mutate();
    } else if (pagoNotaMeson.pagos === 0 && emitiblePagada) emitir.mutate(null);
    else contenedorPago.current?.querySelector<HTMLElement>('[aria-label="Monto 1"]')?.focus();
  };
  // Guardados a la vista (pedido de Rodrigo, 2026-10-23): el mesón ve sus ventas pendientes y la
  // nota de venta sus borradores (las del mesón se retoman en el punto de venta).
  const guardados = useDocumentosGuardados(empresaId);
  const pendientesMeson = (guardados.data?.items ?? []).filter(
    (x) => x.tipo === 'NOTA_VENTA' && (x.canal === 'MESON') === meson && x.id !== notaId,
  );
  // Imprimir: lo guardado (sin cambios) o la previsualización al día; si la nota no está confirmada
  // sale como DOCUMENTO EN CONSTRUCCIÓN, como el borrador de Random.
  const datosImpresion: DatosNotaImpresa | null =
    n && !b.sucio
      ? n
      : editable && vista && !desactualizado
        ? {
            numeroVisible: n?.numeroVisible ?? null,
            cabecera: vista.cabecera,
            lineas: vista.lineas,
            cargos: vista.cargos,
            totales: vista.totales,
          }
        : null;
  const enConstruccion = !n || b.sucio || n.estado === 'BORRADOR' || n.estado === 'EN_APROBACION';

  // ---------------------------------------------------------------- cabecera derivada
  const vendedorId =
    b.vendedorId ||
    n?.cabecera.vendedorId ||
    k?.vendedor.porDefecto ||
    relacion?.vendedor_id ||
    null;
  const vendedor = vendedores.data?.find((v) => v.id === vendedorId) ?? null;
  const lista = listas.data?.find((l) => l.id === listaId) ?? null;
  const direcciones = direccionesElegibles(ficha.data?.direcciones ?? []);
  const direccion =
    (ficha.data?.direcciones ?? []).find((d) => d.id === b.direccionEntregaId) ?? null;
  // Lo que se puede cambiar: SOLO si el backend informa el permiso efectivo (y la nota es editable).
  const cambiaVendedor = editable && !!k?.permisos.cambiarVendedor;
  // Las listas permitidas se ofrecen siempre; sin permiso, elegir otra pide autorización al confirmar.
  const cambiaLista = editable && (k?.listaPrecio.permitidas.length ?? 0) > 1;
  // Líneas digitadas con otra lista que la actual de la nota (no se re-precian solas).
  const lineasOtraLista = b.lineas.filter(
    (l) => l.listaPrecioId && l.listaPrecioId !== listaId,
  ).length;
  const listaPideFirma =
    !k?.permisos.cambiarListaPrecio &&
    [listaId, ...b.lineas.map((l) => l.listaPrecioId ?? listaId)].some(
      (id) => !!id && id !== k?.listaPrecio.porDefecto,
    );
  // Sin el permiso igual se puede pasar a pago en el acto o volver a la del cliente (2026-10-27):
  // lo controlado es dar crédito. Con permiso se ofrece todo lo de la política.
  const condicionTotal = !!k?.permisos.cambiarCondicionPago;
  const cambiaCondicion = editable && !!k;
  const opciones = (k?.condicionPago.opciones ?? [])
    .filter(
      (o) =>
        condicionTotal ||
        CONDICIONES_EN_EL_ACTO.includes(o.condicion) ||
        (o.condicion === k?.condicionPago.porDefecto.condicion &&
          o.plazoDias === k?.condicionPago.porDefecto.plazoDias),
    )
    .map((o) => ({
      valor: valorCondicion(o.condicion, o.plazoDias),
      condicion: o.condicion,
      plazo: o.plazoDias,
      texto: textoCondicion(o.condicion, o.plazoDias),
    }));
  const condicionEfectiva: { condicion: string; plazo: number } = b.condicionPago
    ? { condicion: b.condicionPago, plazo: Number(b.plazoDias || 0) }
    : n
      ? { condicion: n.cabecera.condicionPago, plazo: n.cabecera.plazoDias }
      : k
        ? {
            condicion: k.condicionPago.porDefecto.condicion,
            plazo: k.condicionPago.porDefecto.plazoDias,
          }
        : { condicion: 'contado', plazo: 0 };
  const valorCond = valorCondicion(condicionEfectiva.condicion, condicionEfectiva.plazo);
  const emision = n?.cabecera.fechaNegocio ?? b.fechaNegocio ?? hoyIso();
  // §6: el vencimiento lo calcula el BACKEND (previsualización o nota guardada). El cálculo local solo
  // se muestra mientras el backend responde, marcado como vista previa.
  const vencimientoBackend = editable
    ? (vista?.cabecera.fechaVencimiento ?? null)
    : (n?.cabecera.fechaVencimiento ?? null);
  const vencimientoMostrado = vencimientoBackend ?? vencimiento(emision, condicionEfectiva.plazo);
  // §7: la zona se DERIVA de la dirección de despacho (backend); Comercial solo la ve.
  const zonaNombre =
    (editable ? vista?.cabecera.zonaLogisticaNombre : n?.cabecera.zonaLogisticaNombre) ??
    k?.direcciones.find((d) => d.id === (b.direccionEntregaId || n?.cabecera.direccionEntregaId))
      ?.zona?.nombre ??
    null;
  const digitador = n ? actor(n.digitadoPor) : (usuario?.nombre ?? '—');

  // ---------------------------------------------------------------- precio de lista (pie A lista / Escalas)
  const preciosLista = usePreciosLista(empresaId, listaId, lineasVista);
  const sumas = useMemo(() => {
    if (!calculadas) return null;
    let aLista = 0;
    let escala = 0;
    let completo = true;
    lineasVista.forEach((l, i) => {
      const c = calculadas[i];
      const lista = preciosLista[i]?.data?.valor ?? (c && !c.escalaDesde ? c.precioLista : null);
      const cant = Number(l.cantidad.replace(',', '.'));
      if (!c || !lista || !Number.isFinite(cant)) {
        completo = false;
        return;
      }
      aLista += Number(lista) * cant;
      if (c.escalaDesde && c.precioLista) escala += (Number(lista) - Number(c.precioLista)) * cant;
    });
    const descuentos = calculadas.reduce((s, c) => s + Number(c.descuento || 0), 0);
    return completo ? { aLista, escala, descuentos } : { aLista: null, escala: null, descuentos };
  }, [calculadas, lineasVista, preciosLista]);

  const lineaSel =
    lineasVista.find((l) => l.clave === claveSel) ?? lineasVista[lineasVista.length - 1] ?? null;
  const idxSel = lineaSel ? lineasVista.indexOf(lineaSel) : -1;
  const calcSel = idxSel >= 0 ? (calculadas?.[idxSel] ?? null) : null;

  // ---------------------------------------------------------------- situación comercial (D-108)
  const credito = editable ? vista?.credito : n?.credito.find((c) => c.revision === n.revision);
  const lineaCredito = relacion?.linea_credito ?? null;
  // Sin fuente de CxC (D-108) el «cupo» es la línea entera, sin descontar lo adeudado: no se
  // presenta como disponible (GAP 11b). Con la cartera heredada (D-118) el backend da el disponible
  // ya descontado (línea − deuda − notas que consumen cupo) y la deuda conocida.
  const conFuente = editable && !!vista && vista.credito.fuente !== 'NINGUNA';
  const cupo = conFuente ? (vista?.credito.disponible ?? null) : null;
  const deudaConocida = conFuente ? (vista?.credito.exposicion ?? null) : null;
  const vencidoConocido = conFuente ? (vista?.credito.vencidos ?? null) : null;
  // Mientras no exista la cartera (D-118) solo se muestra la línea aprobada, sin bloques vacíos.
  const sinCxcVisible = !!b.cliente && !conFuente;
  const pedidoActual = totales?.bruto ?? null;
  const despues =
    cupo !== null && pedidoActual !== null ? Number(cupo) - Number(pedidoActual) : null;
  const requeridos = editable ? (vista?.aprobaciones.requeridos ?? []) : [];
  requeridosMeson.current = requeridos;
  const resultadoCredito = editable
    ? vista?.credito.resultado
    : n?.credito.find((c) => c.revision === n.revision)?.resultado;
  const motivosCredito =
    (editable
      ? vista?.credito.motivos
      : (credito as { motivos?: string[] } | undefined)?.motivos) ?? [];
  /**
   * La firma ya se dio (venta 724, 2026-11-02): la evaluación guardada sigue diciendo «requiere
   * autorización», pero la nota confirmada con su firma aprobada ya no la requiere.
   */
  const creditoFirmado =
    !editable &&
    (estado === 'CONFIRMADA' || estado === 'CERRADA_CORTA') &&
    resultadoCredito === 'REQUIERE_AUTORIZACION' &&
    !!n?.solicitudes.some((s) => s.estado === 'APROBADA' && s.revision === n.revision);
  /** «REQUIERE FIRMA» solo mientras se digita o se espera la firma; confirmada ya no la pide. */
  const pideFirma =
    (editable || estado === 'EN_APROBACION') &&
    (requeridos.length > 0 || resultadoCredito === 'REQUIERE_AUTORIZACION');
  // En el punto de venta al contado la franja de crédito no dice nada: aparece solo si es a crédito,
  // si el cliente debe, si hay que firmar o si el cliente tiene una alerta.
  const situacionVisible =
    !meson ||
    condicionEfectiva.condicion !== 'contado' ||
    Number(deudaConocida ?? 0) > 0 ||
    !!alertaCliente ||
    requeridos.length > 0 ||
    (!!resultadoCredito && resultadoCredito !== 'APROBADO');

  return (
    <div className="pantalla nota c21" data-pantalla="nota">
      {avisoGuardada && (
        <div className="insignia tono-aviso" role="status" data-borrador-guardado>
          {avisoGuardada.numero} quedó guardada como borrador.{' '}
          {alAbrirNota && (
            <button
              type="button"
              className="enlace"
              onClick={() => alAbrirNota(avisoGuardada.notaId, avisoGuardada.numero)}
            >
              Abrir
            </button>
          )}{' '}
          <button
            type="button"
            className="enlace"
            aria-label="Cerrar aviso"
            onClick={() => setAvisoGuardada(null)}
          >
            ✕
          </button>
        </div>
      )}
      {/* ------------------------------------------------ cabecera NV: dos filas (ronda 1) */}
      {/* Punto de venta (pedido de Rodrigo, 2026-10-16: «limpiame la ventana»): una sola fila con lo
          que se usa al vender —cliente, vendedor, lista, condición, emisión—; sin despacho, zona,
          vence ni bodega (está en la barra de abajo), y estado y N° solo cuando la venta existe. */}
      <section className={`cab-nv ${meson ? 'pos' : ''}`} aria-label="Cabecera de la nota">
        <div className="fila f1">
          <div className="c cli">
            <Etiqueta
              texto="Cliente"
              marca="edita"
              tecla="F2"
              tip="Escribe el código (RUT sin dígito) y Enter, o un nombre y Enter para ver la lista (F2); en una nota guardada no se cambia"
            />
            <CampoCliente
              key={b.cliente?.id ?? 'sin-cliente'}
              empresaId={empresaId}
              cliente={b.cliente}
              habilitado={editable && !b.notaId}
              editarAlInicio={!b.cliente && !meson && !notaIdInicial}
              tituloBloqueado="El cliente de una nota guardada no se cambia: descarte y cree otra"
              alElegir={elegirCliente}
              alTabulador={() => grilla.current?.enfocarAgregar()}
              alBuscar={(texto) => setDialogo({ tipo: 'cliente', texto })}
              alNoExiste={(rut, dv, existente) =>
                setDialogo({ tipo: 'cliente-no-existe', rut, dv, existente })
              }
            />
          </div>
          {cambiaVendedor ? (
            <label className="c ven">
              <Etiqueta texto="Vendedor" marca="edita" tip="Editable con permiso" />
              <select
                aria-label="Vendedor"
                data-vendedor={vendedorId ?? ''}
                data-motivo={b.cambioVendedor?.motivo ?? ''}
                title={
                  b.cambioVendedor
                    ? `Motivo: ${NOMBRE_MOTIVO_VENDEDOR[b.cambioVendedor.motivo]}`
                    : undefined
                }
                value={vendedorId ?? ''}
                onChange={(e) => {
                  const elegido = e.target.value;
                  // Otro vendedor que el del cliente: primero el motivo (decisión 2026-10-02).
                  if (
                    pideMotivoVendedor(
                      elegido,
                      k!.vendedor.porDefecto,
                      n?.cabecera.vendedorId ?? null,
                    )
                  )
                    setDialogo({ tipo: 'vendedor', vendedorId: elegido });
                  else
                    despachar({
                      tipo: 'cabecera',
                      campos: { vendedorId: elegido, cambioVendedor: null },
                    });
                }}
              >
                {!vendedorId && <option value="">sin vendedor</option>}
                {k!.vendedor.permitidos.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.codigo} · {v.nombre}
                    {v.id === k!.vendedor.porDefecto ? ' (por defecto)' : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="c ven">
              <Etiqueta
                texto="Vendedor"
                marca="auto"
                tip="El del cliente (responsable comercial); quien digita queda en Otros datos"
              />
              <span className="v" data-vendedor={vendedorId ?? ''}>
                {vendedor
                  ? `${vendedor.codigo} · ${vendedor.nombre}`
                  : vendedorId
                    ? '…'
                    : 'sin vendedor'}
              </span>
            </div>
          )}
          {(!meson || n) && (
            <div className="c est">
              <Etiqueta texto={ventaDirectaVista ? 'Estado venta' : 'Estado NV'} />
              <span className="v estado-nv">
                {n ? (
                  <EstadoNota estado={n.estado} />
                ) : (
                  <span className="insignia tono-neutro">Sin guardar</span>
                )}
                {calculo && (
                  <span
                    className={`insignia tono-${calculo.tono}`}
                    data-calculo={prev.estado}
                    title={calculo.texto}
                  >
                    {calculo.texto}
                  </span>
                )}
                <EstadoPago pago={pago.data} />
                <EstadoOperacion op={op === 'listo' && nota.isFetching ? 'cargando' : op} />
              </span>
            </div>
          )}
          <h2 className="solo-lector">
            {n ? n.numeroVisible : directo ? NOMBRE_DIRECTO[directo] : 'Nota de venta nueva'}
          </h2>
          {ventaDirectaVista && n && (
            <div className="c num num-nv" data-documento={docVenta?.folio ?? ''}>
              <Etiqueta
                texto="Documento"
                tip="La boleta o factura de la venta (la venta directa no muestra nota de venta)"
              />
              <span className="v mono">
                {docVenta
                  ? tituloDte(docVenta.tipoDte, docVenta.folio)
                  : meson
                    ? 'Por emitir'
                    : `${nombreDocDirecta === 'boleta' ? 'Boleta' : 'Factura'} por emitir`}
              </span>
            </div>
          )}
          {!(ventaDirectaVista && n) && (!meson || n) && (
            <div className="c num num-nv">
              <Etiqueta
                texto={ventaDirectaVista ? 'Venta N°' : 'Número NV'}
                marca="auto"
                tip="Se asigna al guardar. Escribe otro número y Enter para abrir esa nota"
              />
              <IrANumero
                key={n?.numeroVisible ?? 'nueva'}
                empresaId={empresaId}
                actual={n?.numeroVisible ?? null}
                proximo={proximo.data?.numeroVisible ?? null}
                {...(alAbrirNota ? { alAbrir: alAbrirNota } : {})}
              />
            </div>
          )}
        </div>
        <div className="fila f2">
          {!meson && (
            <div className="c dir" data-entrega={entrega}>
              <Etiqueta
                texto={meson || entrega === 'RETIRO' ? 'Entrega' : 'Dirección despacho'}
                marca="edita"
                tip="Despacho a una dirección del cliente, o el cliente retira en el local"
                extra={
                  !meson &&
                  entrega === 'DESPACHO' &&
                  direccion?.procedencia_estado === 'POR_VALIDAR' && (
                    <span
                      className="insignia tono-aviso"
                      data-procedencia="POR_VALIDAR"
                      title="Dirección heredada del sistema anterior (no distingue despacho de comercial): provisional hasta que MADECI la valide"
                    >
                      por validar
                    </span>
                  )
                }
              />
              {
                // Nota de venta: despacho o retiro · dirección · día de entrega, en una línea. (El
                // punto de venta es entrega inmediata: no muestra este bloque.)
                <div className="fila-entrega">
                  <SelectorEntrega
                    valor={entrega}
                    habilitado={entregaCambiable}
                    alElegir={elegirEntrega}
                  />
                  {entrega === 'RETIRO' ? (
                    <span className="retiro-local" data-retiro-local>
                      Retira en local · no va a despacho
                    </span>
                  ) : (
                    <select
                      aria-label="Dirección de despacho"
                      value={b.direccionEntregaId}
                      disabled={!editable || !direcciones.length}
                      onChange={(e) =>
                        despachar({
                          tipo: 'cabecera',
                          campos: { direccionEntregaId: e.target.value },
                        })
                      }
                    >
                      {!b.direccionEntregaId && (
                        <option value="">{b.cliente ? 'sin dirección' : '—'}</option>
                      )}
                      {direcciones.map((d) => (
                        <option key={d.id} value={d.id}>
                          {tipoDireccion(d)} · {d.nombre ? `${d.nombre} · ` : ''}
                          {textoDireccion(d)}
                          {d.procedencia_estado === 'POR_VALIDAR' ? ' · por validar' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  <label
                    className="fecha-entrega"
                    title="Día en que el cliente recibe o retira (vacío = el mismo día de la nota). Con él se planifica el despacho."
                  >
                    <span>Entrega</span>
                    <input
                      type="date"
                      aria-label="Fecha de entrega"
                      value={fechaEntrega}
                      min={b.fechaNegocio ?? n?.cabecera.fechaNegocio ?? undefined}
                      disabled={!entregaCambiable}
                      onChange={(e) => elegirFechaEntrega(e.target.value)}
                    />
                  </label>
                </div>
              }
            </div>
          )}
          {!meson && (
            <div className="c com">
              <Etiqueta
                texto="Comuna · Zona"
                marca="auto"
                tip="Derivado de la dirección de despacho"
              />
              <span className="v" data-zona={zonaNombre ?? ''}>
                {direccion ? comuna(direccion.comuna_codigo) : '—'}{' '}
                {zonaNombre ? (
                  <span className="chip-zona">{zonaNombre}</span>
                ) : (
                  <span
                    className="chip-zona apagado"
                    title={
                      direccion
                        ? 'La comuna de esta dirección no tiene zona logística configurada'
                        : 'Sin dirección de despacho'
                    }
                  >
                    sin zona
                  </span>
                )}
              </span>
            </div>
          )}
          {cambiaLista ? (
            <label className="c lis">
              <Etiqueta
                texto="Lista"
                marca="edita"
                tip={
                  k?.permisos.cambiarListaPrecio
                    ? 'Editable con permiso'
                    : 'Puedes elegir otra lista: al confirmar pide autorización'
                }
              />
              <select
                aria-label="Lista de precios"
                value={listaId ?? ''}
                onChange={(e) =>
                  despachar({
                    tipo: 'cambiarLista',
                    listaPrecioId: e.target.value,
                    anterior: listaId,
                  })
                }
              >
                {k!.listaPrecio.permitidas.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.codigo} {l.nombre}
                    {l.delCliente ? ' (del cliente)' : ''}
                  </option>
                ))}
              </select>
              {listaPideFirma && (
                <span
                  className="insignia tono-aviso"
                  data-lista-pide-firma
                  title="Al confirmar se pide autorización a quien puede cambiar la lista"
                >
                  pide autorización
                </span>
              )}
            </label>
          ) : (
            <div className="c lis">
              <Etiqueta texto="Lista" marca="auto" tip="La del cliente" />
              <span className="v" data-lista={listaId ?? ''}>
                {lista ? `${lista.codigo} ${lista.nombre}` : listaId ? '…' : 'sin lista'}
              </span>
            </div>
          )}
          <label className="c con">
            <Etiqueta
              texto="Condición"
              marca={cambiaCondicion ? 'edita' : 'auto'}
              tip={
                !cambiaCondicion
                  ? 'La del cliente'
                  : condicionTotal
                    ? `Editable con permiso, hasta el máximo del cliente (${k?.condicionPago.plazoMaximoDias ?? 0} días)`
                    : 'Puedes pasarla a efectivo o transferencia; dar crédito exige permiso'
              }
            />
            <select
              aria-label="Condición de pago"
              value={valorCond}
              disabled={!cambiaCondicion}
              onChange={(e) => {
                const o = opciones.find((x) => x.valor === e.target.value);
                if (o)
                  despachar({
                    tipo: 'cabecera',
                    campos: {
                      condicionPago: o.condicion as CondicionPago,
                      plazoDias: o.plazo ? String(o.plazo) : '',
                    },
                  });
              }}
            >
              {!opciones.some((o) => o.valor === valorCond) && (
                <option value={valorCond}>
                  {NOMBRE_CONDICION[condicionEfectiva.condicion] ?? condicionEfectiva.condicion}
                  {condicionEfectiva.plazo ? ` ${condicionEfectiva.plazo} días` : ''}
                </option>
              )}
              {opciones.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.texto}
                </option>
              ))}
            </select>
          </label>
          <div className="c emi">
            <Etiqueta texto="Emisión" marca="auto" tip="Fecha de negocio" />
            <span className="v">{fecha(emision)}</span>
          </div>
          {(!meson || condicionEfectiva.condicion !== 'contado') && (
            <div className="c vto">
              <Etiqueta texto="Vence" marca="auto" tip="Lo calcula el backend: emisión + plazo" />
              <span
                className={`v ${vencimientoBackend ? '' : 'apagado'}`}
                data-vencimiento={vencimientoBackend ? 'backend' : 'vista-previa'}
                title={
                  vencimientoBackend
                    ? 'Calculado por el backend (emisión + plazo)'
                    : 'Vista previa: el backend lo calcula al previsualizar o guardar'
                }
              >
                {fecha(vencimientoMostrado)}
              </span>
            </div>
          )}
          <div className={`c bodega ${meson ? 'solo-lector' : ''}`}>
            <Etiqueta texto="Bodega" marca="auto" tip="La bodega operativa (por configuración)" />
            <select aria-label="Ubicación" value={b.ubicacionId} disabled className="como-texto">
              {!b.ubicacionId && <option value="">—</option>}
              {deVenta.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.codigo}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>
      {meson && (
        <DocumentoMeson
          documento={docMeson}
          folio={folio.data}
          cargando={folio.isLoading}
          editable={editable || emitibleMeson}
          consumidorFinal={
            !!b.cliente && (b.cliente.rut === '66666666' || b.cliente.id === consumidor.data)
          }
          elegir={(d) =>
            emitibleMeson
              ? setDocElegido(d)
              : despachar({ tipo: 'cabecera', campos: { documento: d } })
          }
          conVale={puedeVale}
          motivoVale={motivoVale}
          alMotivoVale={setMotivoVale}
          empresaId={empresaId}
          {...(alAbrirNota ? { alAbrirNota } : {})}
        />
      )}
      {directo && !meson && (
        // Documento directo: el folio que corresponde, como en Random (y otro número lo llama).
        <div className="documento-meson" aria-label="Documento tributario" data-documento={directo}>
          <span className="etiqueta">{NOMBRE_DIRECTO[directo]}</span>
          <CampoFolio
            empresaId={empresaId}
            tipoDte={tipoDteFolio}
            proximo={folio.data}
            cargando={folio.isLoading}
            {...(alAbrirNota ? { alAbrirNota } : {})}
          />
        </div>
      )}
      {meson && notasPorRetirar.length > 0 && !verPorRetirar && !b.lineas.length && (
        <button
          type="button"
          className="por-retirar"
          data-por-retirar={notasPorRetirar.length}
          onClick={() => setPorRetirarVisto(null)}
        >
          Notas por retirar del cliente ({notasPorRetirar.length})
        </button>
      )}

      {/* ------------------------------------------------ situación comercial: una línea */}
      {situacionVisible && (
        <section className="situacion" aria-label="Situación comercial">
          <div className="b linea-credito">
            <span className="etiqueta">Línea aprobada</span>
            <span className="v">
              {lineaCredito ? pesos(lineaCredito) : b.cliente ? 'sin línea' : '—'}
            </span>
            {sinCxcVisible && (
              <span
                className="sin-cxc"
                title="Sin la cartera de cuentas por cobrar (D-118) no hay utilizado, vencido ni disponible: la venta a crédito requiere autorización"
              >
                CxC sin fuente · disponible no calculable
              </span>
            )}
          </div>
          {deudaConocida !== null && (
            <div
              className={`b ${Number(vencidoConocido ?? 0) > 0 ? 'mal' : ''}`}
              data-deuda={deudaConocida}
            >
              <span className="etiqueta">Deuda</span>
              <span className="v">
                {pesos(deudaConocida)}
                {Number(vencidoConocido ?? 0) > 0 ? ` · vencido ${pesos(vencidoConocido!)}` : ''}
              </span>
            </div>
          )}
          {!sinCxcVisible && cupo !== null && (
            <>
              <div className="b">
                <span className="etiqueta">Disponible</span>
                <span className="v">{pesos(cupo)}</span>
              </div>
              <div className={`b ${despues !== null && despues < 0 ? 'mal' : ''}`}>
                <span className="etiqueta">Disponible después</span>
                <span className="v">{despues !== null ? pesos(String(despues)) : '—'}</span>
              </div>
            </>
          )}
          <div className="b">
            <span className="etiqueta">Pedido actual</span>
            <span className="v">{pedidoActual ? pesos(pedidoActual) : '—'}</span>
          </div>
          <div
            className={`alerta tono-${resultadoCredito === 'RECHAZADO' ? 'malo' : creditoFirmado ? 'neutro' : requeridos.length || (resultadoCredito && resultadoCredito !== 'APROBADO') ? 'aviso' : 'neutro'}`}
            data-situacion={resultadoCredito ?? ''}
          >
            {alertaCliente ? (
              <b className="texto-malo">
                Cliente{' '}
                {relacion?.bloqueado || b.cliente?.bloqueado ? 'BLOQUEADO' : b.cliente?.estado}
                {relacion?.motivo_bloqueo ? `: ${relacion.motivo_bloqueo}` : ''}
              </b>
            ) : creditoFirmado ? (
              <>
                <span className="insignia tono-ok" data-credito="FIRMADO">
                  Autorizada con firma
                </span>
                <span>{motivosCredito.map((m) => MOTIVOS_CREDITO[m] ?? m).join(' · ')}</span>
              </>
            ) : resultadoCredito && resultadoCredito !== 'APROBADO' ? (
              <>
                <EstadoCredito resultado={resultadoCredito} />
                <span>
                  {motivosCredito.includes('SIN_FUENTE_CXC') && (
                    <b>Requiere firma para confirmar esta {ventaDirectaVista ? 'venta' : 'NV'}. </b>
                  )}
                  {motivosCredito.map((m) => MOTIVOS_CREDITO[m] ?? m).join(' · ')}
                </span>
              </>
            ) : requeridos.length ? (
              <b>Requiere firma: {requeridos.map((a) => NOMBRE_AMBITO[a] ?? a).join(', ')}</b>
            ) : (
              <span className="apagado">
                {b.cliente
                  ? vista
                    ? 'Crédito sin observaciones.'
                    : 'El crédito lo evalúa el backend al calcular.'
                  : 'Elija el cliente (F2).'}
              </span>
            )}
            {/* Cobro contra entrega (0107, Rodrigo 2026-10-24): quien toma un pedido a despacho
                puede desbloquear la morosidad o el crédito sobre crédito declarando que el cliente
                paga lo pendiente al recibir; la ODD lo cobra y lleva la hoja de cobranza. */}
            {editable &&
              !meson &&
              b.entrega !== 'RETIRO' &&
              vista &&
              (b.cobroContraEntrega ||
                vista.credito.motivos.some(
                  (m) => m === 'DEUDA_VENCIDA' || m === 'CREDITO_SOBRE_CREDITO',
                )) && (
                <button
                  type="button"
                  className={`boton-contra-entrega${b.cobroContraEntrega ? ' activo' : ''}`}
                  aria-pressed={!!b.cobroContraEntrega}
                  title="El cliente paga lo pendiente al recibir este pedido: transporte lleva la hoja de cobranza"
                  onClick={() =>
                    despachar({
                      tipo: 'cabecera',
                      campos: { cobroContraEntrega: !b.cobroContraEntrega },
                    })
                  }
                >
                  {b.cobroContraEntrega
                    ? '✓ Cobra lo pendiente al entregar (quitar)'
                    : 'Cliente paga lo pendiente al recibir (desbloquear)'}
                </button>
              )}
            {/* Los demás avisos del cálculo van aquí arriba, no bajo las líneas (2026-10-24). */}
            {editable && vista && (
              <AvisosCalculo
                vista={vista}
                conFirma={!!resultadoCredito && resultadoCredito !== 'APROBADO'}
              />
            )}
          </div>
        </section>
      )}

      {resultado && <ResultadoConfirmacion r={resultado} alCerrar={() => setResultado(null)} />}
      {firma && !esperandoFirma && (
        <div
          className={`aviso-linea tono-${firma.resultado === 'APROBADA' ? 'ok' : 'malo'}`}
          role="status"
          data-firma={firma.resultado}
        >
          {n?.ventaDirecta && !meson ? 'Venta' : n?.numeroVisible}:{' '}
          {firma.resultado === 'APROBADA'
            ? notaPorEmitir
              ? `el supervisor AUTORIZÓ la venta: cóbrala con F10 y sale la ${NOMBRE_DOC_POS[docMeson].toLowerCase()}.`
              : emitibleDirecta
                ? cobrarAntesDeEmitir
                  ? `el supervisor AUTORIZÓ la venta: cóbrala con F10 y emite la ${nombreDocDirecta}.`
                  : `el supervisor AUTORIZÓ la venta: emite la ${nombreDocDirecta} con F10.`
                : 'el supervisor AUTORIZÓ la nota; quedó confirmada.'
            : `el supervisor la RECHAZÓ${firma.motivo ? `: «${firma.motivo}»` : ''}. Ajústala y vuelve a confirmar.`}{' '}
          <button
            type="button"
            className="boton-icono"
            onClick={() => setFirma(null)}
            aria-label="Cerrar aviso"
          >
            ✕
          </button>
        </div>
      )}

      {/* ------------------------------------------------ área de trabajo: grilla 70 % + panel 30 % */}
      <div className="trabajo-nv">
        {(problemas.length > 0 ||
          (editable && prev.error && prev.estado === 'error') ||
          !!error ||
          (editable && vista)) && (
          <div className="avisos-nv" aria-live="polite">
            {problemas.length > 0 && (
              <ul className="problemas" role="alert">
                {problemas.map((p) => (
                  <li key={p.campo + p.mensaje}>{p.mensaje}</li>
                ))}
              </ul>
            )}
            {editable && prev.error && prev.estado === 'error' && (
              <PanelError error={prev.error}>
                <p className="ayuda">
                  La nota no se guardó ni cambió: corrija y el cálculo se repite solo.
                </p>
              </PanelError>
            )}
            {!!error && <PanelError error={error} alCerrar={() => setError(null)} />}
          </div>
        )}
        <GrillaLineas
          ref={grilla}
          {...(meson && editable && puede('caja.recibo.registrar')
            ? {
                alTabulador: () =>
                  contenedorPago.current
                    ?.querySelector<HTMLElement>('[aria-label="Medio 1"]')
                    ?.focus(),
              }
            : {})}
          // Última línea + Tab → «Confirmar», y Enter confirma igual que F9 (pedido de Rodrigo, 2026-10-23).
          alTabularFinal={() => botonConfirmar.current?.focus()}
          empresaId={empresaId}
          listaId={listaId}
          codigoLista={(id) => k?.listaPrecio.permitidas.find((x) => x.id === id)?.codigo ?? null}
          lineas={lineasVista}
          calculadas={calculadas}
          reservas={soloLectura ? (n?.reservas.lineas ?? null) : null}
          obsoleto={desactualizado}
          soloLectura={!editable}
          puedeAgregar={editable && !!b.cliente}
          // Todos pueden poner un precio manual; sin el permiso, pide autorización en línea.
          puedePrecioManual
          precioManualConAutorizacion={!puede('ventas.precio.manual')}
          disponibilidad={stock.data}
          pedido={vista?.disponibilidad ?? null}
          textoAgregar={textoAgregar}
          alCambiarTextoAgregar={setTextoAgregar}
          alBuscarProducto={(texto) => void ingresarCodigo(texto)}
          alCambiar={(clave, campos) => despachar({ tipo: 'linea', clave, campos })}
          alQuitar={pedirQuitar}
          alSeleccionar={seleccionar}
          alVerProducto={verProducto}
          claveSeleccionada={lineaSel?.clave ?? null}
        />
        <PanelContexto
          empresaId={empresaId}
          clienteId={b.cliente?.id ?? null}
          {...(alAbrirFicha && b.cliente
            ? {
                alAbrirFicha: () => b.cliente && alAbrirFicha(b.cliente.id, b.cliente.razonSocial),
              }
            : {})}
          ficha={ficha.data}
          vendedorNombre={vendedor ? `${vendedor.codigo} · ${vendedor.nombre}` : null}
          zonaNombre={zonaNombre}
          listaNombre={lista ? `${lista.codigo} ${lista.nombre}` : null}
          listaId={listaId}
          ubicacionId={b.ubicacionId || null}
          direccion={direccion}
          pestana={pestana}
          alCambiarPestana={setPestana}
          linea={
            lineaSel
              ? {
                  productoId: lineaSel.productoId,
                  presentacionId: lineaSel.presentacionId,
                  sku: lineaSel.sku,
                  nombre: lineaSel.nombre,
                  calculada: calcSel,
                }
              : null
          }
          notaActual={n}
          alAbrirNota={alAbrirNota}
          enfocarResultado={enfocarResultado}
          stockBodega={stock.data}
        />
      </div>

      {conPagoMeson && (
        <div ref={contenedorPago} className="pago-meson-envoltura">
          <PagoMeson
            total={cobroNotaMeson ? (saldoMeson ?? 0) : totalVenta}
            {...(cobroNotaMeson ? { etiquetaTotal: 'Por pagar' } : {})}
            desglose={
              totales
                ? [
                    ...(cargos ?? []).map((c) => ({
                      etiqueta: c.tipo === 'FLETE_VENTA' ? 'Flete' : c.descripcion,
                      monto: c.bruto,
                    })),
                    { etiqueta: 'Neto', monto: totales.neto },
                    ...totales.impuestos.map((i) => ({ etiqueta: i.codigo, monto: i.monto })),
                    ...(cobroNotaMeson && pago.data && Number(pago.data.pagado) > 0
                      ? [
                          { etiqueta: 'Total nota', monto: pago.data.total },
                          { etiqueta: 'Ya pagado', monto: pago.data.pagado },
                        ]
                      : []),
                  ]
                : []
            }
            aviso={
              cobroNotaMeson ? (
                emitiblePagada ? (
                  <span className="firma">SIN PAGO AUTORIZADO</span>
                ) : null
              ) : desactualizado && totales ? (
                <span className="apagado">Recalculando…</span>
              ) : pideFirma ? (
                <span className="firma">REQUIERE FIRMA</span>
              ) : null
            }
            filas={filasPago}
            sinTurno={sinTurnoMeson}
            deshabilitado={guardar.isPending || transicion.isPending || cobroMeson.isPending}
            alCambiar={setFilasPago}
            alIrAGrabar={() =>
              setTimeout(() => (cobroNotaMeson ? botonCobrarNota : botonGrabar).current?.focus(), 0)
            }
            {...(puede('caja.turno.operar') ? { alAbrirCaja: () => setAvisoCaja('abrir') } : {})}
          />
        </div>
      )}

      {/* ------------------------------------------------ pie de totales (106) */}
      {/* Punto de venta: el total, neto e impuestos van en el resumen de cuenta (2026-10-24). */}
      {!conPagoMeson && (
        <PieTotales
          compacto={meson}
          totales={totales}
          cargos={cargos}
          sumas={sumas}
          obsoleto={desactualizado}
          editable={editable}
          lineas={lineasVista.length}
          despues={despues}
          requiereFirma={pideFirma}
          titulo={
            meson
              ? 'TOTAL VENTA'
              : ventaDirectaVista
                ? `TOTAL ${(n?.cabecera.documentoSolicitado ?? b.documento) === 'BOLETA' ? 'BOLETA' : 'FACTURA'}`
                : 'TOTAL NV'
          }
        />
      )}

      {/* ------------------------------------------------ barra de atajos y acciones (52) */}
      <footer className="barra-atajos-nv">
        {/* Las acciones van al centro (Rodrigo 2026-10-21: «muy tiradas a la izquierda»). */}
        <span className="espaciador" />
        {editable && (
          <button
            type="button"
            onClick={recalcular}
            data-recalcular-lista={lineasOtraLista > 0 ? '' : undefined}
            title={
              lineasOtraLista > 0
                ? `Pasa ${lineasOtraLista} ${lineasOtraLista === 1 ? 'línea' : 'líneas'} a la lista de la nota y las vuelve a preciar. No guarda nada.`
                : 'Calcula precios, impuestos y crédito. No guarda nada.'
            }
          >
            <kbd>F8</kbd>Calcular
            {lineasOtraLista > 0 ? ` con esta lista (${lineasOtraLista})` : ''}
          </button>
        )}
        {meson && estado === 'BORRADOR' && puede('ventas.nota.confirmar') && (
          <button
            ref={botonGrabar}
            type="button"
            data-grabar-meson
            className={`cobrar ${requeridos.length ? 'firma' : ''}`}
            onClick={() => void cobrarYEmitir()}
            disabled={
              guardar.isPending ||
              transicion.isPending ||
              cobroMeson.isPending ||
              !b.lineas.length ||
              (puede('caja.recibo.registrar') && desactualizado)
            }
            title={
              pagoMeson.listo || !puede('caja.recibo.registrar')
                ? 'Graba la venta, registra el pago y emite la boleta/factura para imprimir'
                : pagoMeson.pagos === 0
                  ? 'Sin pagos: abre la caja o emite sin pago (con autorización)'
                  : 'Los pagos deben sumar la venta (saldo $0)'
            }
          >
            <kbd>F4</kbd>Grabar e imprimir{requeridos.length ? ' · requiere firma' : ''}
          </button>
        )}
        {editable && (
          // Observación a la vista (Rodrigo, 2026-10-23): antes solo estaba en «Más › Otros datos».
          <button
            type="button"
            onClick={() => setDialogo({ tipo: 'observacion' })}
            title={
              b.observaciones
                ? `Observación: ${b.observaciones}`
                : 'Agregar una observación (sale impresa en el documento)'
            }
            data-con-observacion={b.observaciones ? 'true' : undefined}
          >
            <kbd>Alt+B</kbd>
            {b.observaciones ? 'Observación ✓' : 'Observación'}
          </button>
        )}
        {guardableBorrador && (
          <button
            type="button"
            onClick={() => void guardarYLimpiar()}
            disabled={guardar.isPending || !b.lineas.length}
            title={
              meson
                ? 'Guarda la venta como pendiente y deja la pantalla libre (se retoma en «Pendientes»)'
                : 'Guarda la nota como borrador y deja la pantalla libre (se retoma en «Guardados»)'
            }
          >
            <kbd>Alt+G</kbd>
            {meson ? 'Dejar pendiente' : 'Guardar borrador'}
          </button>
        )}
        <button
          type="button"
          onClick={() => setVerPendientes(true)}
          title={
            meson
              ? 'Ventas de mesón guardadas como pendientes'
              : 'Notas de venta guardadas en borrador'
          }
        >
          {meson ? 'Pendientes' : 'Guardados'}
          {pendientesMeson.length ? ` (${pendientesMeson.length})` : ''}
        </button>
        {!meson && estado === 'BORRADOR' && puede('ventas.nota.confirmar') && (
          <button
            ref={botonConfirmar}
            type="button"
            className={`primario ${requeridos.length ? 'firma' : ''}`}
            onClick={() => void abrirConfirmacion()}
            disabled={guardar.isPending || transicion.isPending || !b.lineas.length}
          >
            <kbd>F9</kbd>Confirmar{requeridos.length ? ' · requiere firma' : ''}
          </button>
        )}
        {cobroNotaMeson && (
          <button
            ref={botonCobrarNota}
            type="button"
            className="cobrar"
            data-cobrar-nota-meson
            onClick={cobrarNotaMeson}
            disabled={cobroMeson.isPending}
            title={
              pagoNotaMeson.listo
                ? 'Registra el pago y emite el documento para imprimir'
                : pagoNotaMeson.pagos === 0 && emitiblePagada
                  ? 'Sin pagos: sale con la autorización para emitir sin pago'
                  : 'Ingresa el pago abajo: debe sumar el saldo de la nota'
            }
          >
            <kbd>F10</kbd>
            {pagoNotaMeson.pagos === 0 && emitiblePagada
              ? `Emitir ${NOMBRE_DOC_POS[docMeson].toLowerCase()} · sin pago autorizado`
              : `Cobrar y emitir ${NOMBRE_DOC_POS[docMeson].toLowerCase()}`}
          </button>
        )}
        {emitiblePagada && !cobroNotaMeson && (
          <button
            type="button"
            className="cobrar"
            onClick={() => emitir.mutate(null)}
            title={
              saldoMeson === 0
                ? 'La nota está pagada: se emite el documento'
                : 'Emitir sin pago: autorizado'
            }
          >
            <kbd>F10</kbd>Emitir {NOMBRE_DOC_POS[docMeson].toLowerCase()}
            {saldoMeson === 0 ? ' · pagada' : ' · sin pago autorizado'}
          </button>
        )}
        {directaPorEmitir && (
          <button
            type="button"
            className={cobrarAntesDeEmitir ? undefined : 'primario'}
            data-emitir-directa
            disabled={!emitibleDirecta || emitirDirecto.isPending}
            onClick={abrirEmitirDirecta}
            title={
              sinEmision
                ? 'En preproducción no se emite: el documento real se emite en producción'
                : !puede('dte.solicitar')
                  ? 'Sin permiso para emitir documentos tributarios (dte.solicitar)'
                  : cobrarAntesDeEmitir
                    ? `Se la lleva ahora: cobra primero (F10) y emite la ${nombreDocDirecta}`
                    : `Emite la ${nombreDocDirecta} de esta venta`
            }
          >
            {!cobrarAntesDeEmitir && <kbd>F10</kbd>}Emitir {nombreDocDirecta}
          </button>
        )}
        {cobrable && !emitiblePagada && !cobroNotaMeson && !(meson && estado === 'BORRADOR') && (
          <button
            type="button"
            className="cobrar"
            onClick={() => setDialogo({ tipo: 'cobrar' })}
            title="Mesón: cobrar el pago anticipado de la nota (recibo de caja)"
          >
            {!(emitibleDirecta && !cobrarAntesDeEmitir) && <kbd>F10</kbd>}Cobrar · saldo{' '}
            {pesos(pago.data!.saldo)}
          </button>
        )}
        {emitibleSinPago && (puede('caja.recibo.registrar') || puede('dte.solicitar')) && (
          <button
            type="button"
            className="firma"
            data-emitir-sin-pago
            disabled={pedirSinPago.isPending || esperandoSinPago}
            onClick={emitirSinPago}
            title="El documento sale sin su pago: queda registrado quién lo pidió y quién lo autorizó"
          >
            {puede('ventas.documento.sin_pago')
              ? 'Emitir sin pago'
              : 'Emitir sin pago · pedir autorización'}
          </button>
        )}
        {estado === 'CONFIRMADA' && !meson && !enModificacion && puede('ventas.nota.revisar') && (
          <button
            type="button"
            onClick={() => {
              if (n) despachar({ tipo: 'cargar', borrador: borradorDesdeNota(n) });
              setModificando(true);
            }}
            title="Cambiar líneas, cantidades o datos de la nota confirmada (mientras no haya salido nada)"
          >
            Modificar
          </button>
        )}
        {enModificacion && (
          <>
            <button
              type="button"
              className="primario"
              disabled={guardar.isPending || !b.lineas.length}
              onClick={() => guardar.mutate()}
            >
              {guardar.isPending ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button
              type="button"
              disabled={guardar.isPending}
              onClick={() => {
                if (n) despachar({ tipo: 'cargar', borrador: borradorDesdeNota(n) });
                setModificando(false);
              }}
            >
              Cancelar cambios
            </button>
          </>
        )}
        {documentosVenta.map((d) => (
          <span key={d.documentoId} className="grupo-botones">
            <button
              type="button"
              className="primario"
              disabled={!d.folio}
              title={d.folio ? 'Directo a la impresora' : 'El documento todavía no tiene folio'}
              onClick={() => {
                setErrorDte(null);
                imprimirDocumento(d.documentoId).catch(setErrorDte);
              }}
            >
              Imprimir {tituloDte(d.tipoDte, d.folio)}
            </button>
            <button
              type="button"
              disabled={!d.folio}
              title="Abre el PDF en otra pestaña"
              onClick={() => {
                setErrorDte(null);
                verPdfDte(d.documentoId).catch(setErrorDte);
              }}
            >
              Ver
            </button>
            {alAbrirDte && (
              <button
                type="button"
                title="La ficha del documento: detalle, estado SII, cedible, XML y notas de crédito"
                onClick={() => alAbrirDte(d.documentoId, tituloDte(d.tipoDte, d.folio))}
              >
                Abrir documento
              </button>
            )}
          </span>
        ))}
        {!!errorDte && <PanelError error={errorDte} />}
        {/* La venta directa no se imprime como NV: al cliente solo su factura o boleta. */}
        {!ventaDirectaVista &&
          !documentosVenta.length &&
          (datosImpresion || lineasVista.length > 0) && (
            <button
              type="button"
              disabled={!datosImpresion}
              title={
                datosImpresion
                  ? enConstruccion
                    ? 'Imprime la nota como DOCUMENTO EN CONSTRUCCIÓN (todavía no está confirmada)'
                    : 'Imprime la nota de venta'
                  : 'Calcula primero (F8) para imprimir'
              }
              onClick={() => setImprimirNv(true)}
            >
              Imprimir{enConstruccion ? ' (en construcción)' : ' NV'}
            </button>
          )}
        {limpiable && (
          <button
            type="button"
            onClick={limpiar}
            title="Deja la pantalla en blanco: lo no guardado se pierde; lo guardado sigue guardado"
          >
            <kbd>Alt+L</kbd>Limpiar pantalla
          </button>
        )}
        {estado === 'EN_APROBACION' && solicitudPendiente && puede('ventas.nota.aprobar') && (
          <button
            type="button"
            className="primario"
            onClick={() => setDialogo({ tipo: 'motivo', accion: 'aprobar' })}
          >
            Aprobar
          </button>
        )}
        {estado === 'EN_APROBACION' && solicitudPendiente && puede('ventas.nota.rechazar') && (
          <button
            type="button"
            className="peligro"
            onClick={() => setDialogo({ tipo: 'motivo', accion: 'rechazar' })}
          >
            Rechazar
          </button>
        )}
        {estado === 'CONFIRMADA' && n && alAbrirDespacho && !directaPorEmitir && (
          <button type="button" onClick={() => alAbrirDespacho(n.notaId, n.numeroVisible)}>
            Ir a despacho <kbd>Alt+P</kbd>
          </button>
        )}
        {/* En el mesón no hay «Más ▾»: la venta que no se concretó se elimina aquí (Rodrigo, 2026-10-26). */}
        {meson &&
          (estado === 'CONFIRMADA' || estado === 'EN_APROBACION') &&
          !documentosVenta.length &&
          puede('ventas.nota.anular') && (
            <button
              type="button"
              className="peligro"
              disabled={transicion.isPending}
              onClick={() => setDialogo({ tipo: 'motivo', accion: 'anular' })}
              title="Elimina la venta (queda ANULADA y se libera la reserva). Solo si no tiene documento emitido ni salió mercadería"
            >
              Eliminar
            </button>
          )}
        <span className="espaciador" />
        {!meson && (
          <span className="menu-mas-nv">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={masAbierto}
              onClick={() => setMasAbierto((x) => !x)}
              title="Otros datos, guardar borrador…"
            >
              Más ▾
            </button>
            {masAbierto && (
              <span
                className="menu-mas-nv-lista"
                role="menu"
                aria-label="Más acciones"
                onClick={() => setMasAbierto(false)}
              >
                {editable && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => setDialogo({ tipo: 'otros' })}
                    title="Documento, flete y observaciones"
                  >
                    Otros datos · canal <kbd>Alt+M</kbd>
                  </button>
                )}
                {guardableBorrador && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void guardarYLimpiar()}
                    disabled={guardar.isPending || !b.lineas.length}
                  >
                    {guardar.isPending ? 'Guardando…' : 'Guardar borrador'}
                  </button>
                )}
                {documentosVenta.length > 0 && datosImpresion && (
                  <button
                    type="button"
                    role="menuitem"
                    title="El registro interno de la venta (no es el documento tributario)"
                    onClick={() => setImprimirNv(true)}
                  >
                    Imprimir nota de venta (interna)
                  </button>
                )}
                {estado === 'CONFIRMADA' && pendientes && puede('ventas.reserva.crear_manual') && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => setDialogo({ tipo: 'motivo', accion: 'reservar' })}
                  >
                    Reservar pendiente
                  </button>
                )}
                {estado === 'BORRADOR' && b.notaId && puede('ventas.nota.editar') && (
                  <button
                    type="button"
                    role="menuitem"
                    className="peligro"
                    disabled={transicion.isPending}
                    onClick={() => transicion.mutate({ accion: 'descartar' })}
                  >
                    Descartar borrador
                  </button>
                )}
                {(estado === 'CONFIRMADA' || estado === 'EN_APROBACION') &&
                  puede('ventas.nota.anular') && (
                    <button
                      type="button"
                      role="menuitem"
                      className="peligro"
                      disabled={transicion.isPending}
                      onClick={() => setDialogo({ tipo: 'motivo', accion: 'anular' })}
                      title="Solo si no tiene documento tributario vigente, ni va en una ODD, ni salió mercadería"
                    >
                      Eliminar
                    </button>
                  )}
                {(estado === 'CONFIRMADA' || estado === 'EN_APROBACION') &&
                  n &&
                  puede('ventas.nota.anular') &&
                  puede('ventas.nota.crear') && (
                    <button
                      type="button"
                      role="menuitem"
                      className="peligro"
                      disabled={transicion.isPending || eliminarYGuardar.isPending}
                      onClick={() => setDialogo({ tipo: 'motivo', accion: 'anular-guardar' })}
                      title="Como en Random: la nota se elimina y queda una copia en borrador para usarla de nuevo"
                    >
                      Eliminar y guardar
                    </button>
                  )}
                <span className="atajos-nv-ayuda">
                  F2 cliente · F3 producto · F5 cartola · F6 pedidos · F7 últimas compras · Supr
                  quita la línea
                </span>
              </span>
            )}
          </span>
        )}
        {imprimirNv && datosImpresion && (
          <Imprimir alTerminar={() => setImprimirNv(false)}>
            <NotaImpresa
              n={datosImpresion}
              empresa={empresa ?? null}
              vendedor={vendedor?.nombre ?? null}
              enConstruccion={enConstruccion}
            />
          </Imprimir>
        )}
        <span className="estado-guardado">
          {guardadoEn ? (
            <>
              Guardado <b>{guardadoEn}</b>
            </>
          ) : b.sucio && n ? (
            <span className="insignia tono-aviso">Cambios sin guardar</span>
          ) : null}
          {tomadaDeGuardados && n?.estado === 'BORRADOR' && !tomada.current.reguardada && (
            <span
              className="insignia tono-aviso"
              title="Tomada de los guardados: si limpias o cierras sin volver a guardarla, se elimina"
            >
              En construcción · Alt+G para dejarla guardada
            </span>
          )}
          {n ? (
            <EstadoNota estado={n.estado} />
          ) : meson ? null : (
            <span className="insignia tono-neutro">NV borrador</span>
          )}
          {n && (
            <span className="apagado">
              rev. {n.revision} · v{n.version}
            </span>
          )}
        </span>
      </footer>

      {/* ------------------------------------------------ diálogos */}
      {dialogo?.tipo === 'cliente-no-existe' && (
        <ClienteNoExiste
          empresaId={empresaId}
          rut={dialogo.rut}
          dv={dialogo.dv}
          existente={dialogo.existente}
          alVinculado={(c) => {
            setDialogo(null);
            elegirCliente(c);
          }}
          puedeCrear={puede('entidades.gestionar') && puede('entidades.global.gestionar')}
          alCrear={() => setDialogo({ tipo: 'cliente-nuevo', rut: dialogo.rut, dv: dialogo.dv })}
          alBuscar={() => setDialogo({ tipo: 'cliente', texto: '' })}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo?.tipo === 'cliente-nuevo' && (
        <ClienteNuevo
          empresaId={empresaId}
          rutInicial={dialogo.rut}
          dvInicial={dialogo.dv}
          alCreado={(c) => {
            setDialogo(null);
            elegirCliente(c);
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo?.tipo === 'cliente' && (
        <BuscadorCliente
          empresaId={empresaId}
          textoInicial={dialogo.texto ?? ''}
          alElegir={elegirCliente}
          alCerrar={() => setDialogo(null)}
          alNuevo={
            puede('entidades.gestionar') && puede('entidades.global.gestionar')
              ? (t) => {
                  // Si lo escrito es un RUT, el formulario nace con él.
                  const m = /^\s*([\d.]{7,12})-?([\dkK])?\s*$/.exec(t);
                  setDialogo({
                    tipo: 'cliente-nuevo',
                    rut: m ? m[1]!.replace(/\D/g, '') : '',
                    dv: m?.[2]?.toUpperCase() ?? null,
                  });
                }
              : undefined
          }
        />
      )}
      {dialogo?.tipo === 'producto' && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={listaId}
          ubicacionId={b.ubicacionId}
          ubicaciones={ubicaciones.data ?? []}
          textoInicial={dialogo.texto}
          alCerrar={() => setDialogo(null)}
          alElegir={({ producto, presentacion, base }) => {
            setDialogo(null);
            agregarLinea(
              producto,
              base,
              presentacion.id === base.id
                ? null
                : { codigo: presentacion.codigo, factor: presentacion.factor_a_base },
            );
          }}
        />
      )}
      {dialogo?.tipo === 'cobrar' && pago.data && b.cliente && (
        <CobrarNota
          empresaId={empresaId}
          clienteEntidadId={b.cliente.id}
          pago={pago.data}
          puedeAnular={puede('caja.recibo.anular')}
          puedeOperarCaja={puede('caja.turno.operar')}
          alCobrado={setReciboMeson}
          alCerrar={() => {
            setDialogo(null);
            // Punto de venta: pagada completa → se pide la boleta/factura en el acto.
            if (meson && reciboMeson?.estadoPago === 'PAGADA') {
              emitir.mutate(reciboMeson);
              return;
            }
            // Venta directa pagada: sigue a emitir su factura o boleta.
            if (emitibleDirecta && reciboMeson?.estadoPago === 'PAGADA') {
              abrirEmitirDirecta();
              return;
            }
            if (nuevaTrasCobro) alNuevaNota?.();
          }}
        />
      )}
      {dialogo?.tipo === 'emitir-directa' && n && (
        <EmitirVentaDirecta
          directo={n.cabecera.documentoSolicitado === 'BOLETA' ? 'BOLETA' : 'FACTURA'}
          emision={emisionDirecta}
          alEmitir={(inmediato) => emitirDirecto.mutate(inmediato)}
          {...(emisionDirecta?.dteId
            ? { alImprimir: () => void imprimirDte(emisionDirecta.dteId!) }
            : {})}
          errorImpresion={errorImpresion}
          alCerrar={() => {
            if (emitirDirecto.isPending) return;
            setDialogo(null);
          }}
        />
      )}
      {verPendientes && (
        <Modal
          titulo={meson ? 'Ventas pendientes del mesón' : 'Notas de venta guardadas (borrador)'}
          ancho="medio"
          alCerrar={() => setVerPendientes(false)}
        >
          {pendientesMeson.length === 0 ? (
            <p className="ayuda">
              {meson
                ? 'No hay ventas pendientes. «Dejar pendiente» (Alt+G) guarda una aquí.'
                : 'No hay notas guardadas. «Guardar borrador» (Alt+G) guarda una aquí.'}
            </p>
          ) : (
            <table className="grilla compacta" aria-label="Ventas pendientes">
              <thead>
                <tr>
                  <th>{meson ? 'Venta' : 'Nota'}</th>
                  <th>Documento</th>
                  <th>Cliente</th>
                  <th className="al-der">Total</th>
                  <th>Guardada</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pendientesMeson.map((x, i) => (
                  <tr key={x.id}>
                    <td className="mono">{x.numero}</td>
                    <td>{x.documento === 'FACTURA' ? 'Factura' : 'Boleta'}</td>
                    <td>{x.contraparte}</td>
                    <td className="al-der mono">{x.total ? pesos(x.total) : '—'}</td>
                    <td className="apagado">
                      {x.actualizadoPorNombre ?? ''}{' '}
                      {x.actualizadoEn ? fechaHora(x.actualizadoEn) : ''}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="primario"
                        {...(i === 0 ? { 'data-autofoco': true } : {})}
                        onClick={() => {
                          setVerPendientes(false);
                          // Se retoma en esta misma pestaña (o en otra si la pantalla no lo permite).
                          if (alTomarNota) alTomarNota(x.id, x.numero ?? '', true);
                          else alAbrirNota?.(x.id, x.numero ?? '');
                        }}
                      >
                        Retomar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
      {verPorRetirar && b.cliente && (
        <NotasPorRetirar
          cliente={b.cliente.razonSocial}
          notas={notasPorRetirar}
          alTomar={(x) => void tomarNota(x)}
          alCerrar={() => setPorRetirarVisto(clientePorRetirar)}
        />
      )}
      {ventaMeson && n && (
        <VentaMesonTerminada
          documento={docVentaMeson ?? n.cabecera.documentoSolicitado}
          venta={ventaMeson}
          reintentar={() => emitir.mutate(ventaMeson.recibo)}
          {...(ventaMeson.dteId ? { alImprimir: () => void imprimirDte(ventaMeson.dteId!) } : {})}
          {...(ventaMeson.valeId ? { alImprimir: () => setImprimirVale(ventaMeson.valeId!) } : {})}
          {...(ventaMeson.valeNumero ? { valeNumero: ventaMeson.valeNumero } : {})}
          errorImpresion={errorImpresion}
          vuelto={vueltoMeson}
          {...(alNuevaNota ? { alNueva: alNuevaNota } : {})}
          alQuedarse={() => setVentaMeson(null)}
        />
      )}
      {imprimirVale && (
        <ImprimirVale valeId={imprimirVale} alTerminar={() => setImprimirVale(null)} />
      )}
      {terminada && alNuevaNota && (
        <NotaTerminada
          r={terminada}
          {...(directo ? { directo } : {})}
          sinEmision={sinEmision}
          {...(directo && directo !== 'GUIA' && !sinEmision && puede('dte.solicitar')
            ? {
                emisionDirecta: emisionDirecta,
                alEmitir: (inmediato: boolean) => emitirDirecto.mutate(inmediato),
                ...(emisionDirecta?.dteId
                  ? { alImprimir: () => void imprimirDte(emisionDirecta.dteId!) }
                  : {}),
                errorImpresion,
              }
            : {})}
          {...(directo === 'GUIA' && alAbrirDespacho && !sinEmision
            ? {
                alDespachar: () => {
                  setTerminada(null);
                  alAbrirDespacho(terminada.notaId, terminada.numeroVisible);
                },
              }
            : {})}
          cobrable={cobrable}
          saldo={pago.data?.saldo ?? null}
          alNueva={alNuevaNota}
          alCobrar={() => {
            setTerminada(null);
            setNuevaTrasCobro(true);
            setDialogo({ tipo: 'cobrar' });
          }}
          alQuedarse={() => {
            setTerminada(null);
            setEnfocarResultado((x) => x + 1);
          }}
        />
      )}
      {dialogo?.tipo === 'otros' && (
        <Modal
          titulo="Otros datos de la nota (Alt+M)"
          ancho="angosto"
          alCerrar={() => setDialogo(null)}
        >
          <label className="campo">
            <span className="etiqueta">Canal (origen de la venta)</span>
            <select
              aria-label="Canal"
              value={b.canal}
              data-autofoco
              disabled={!editable || !!b.notaId}
              title={
                b.notaId
                  ? 'El canal de una nota guardada no cambia'
                  : 'Por defecto, el del origen (call center, mesón, vendedor)'
              }
              onChange={(e) =>
                despachar({ tipo: 'cabecera', campos: { canal: e.target.value as never } })
              }
            >
              {CANALES.map((c) => (
                <option key={c} value={c}>
                  {NOMBRE_CANAL[c]}
                </option>
              ))}
            </select>
          </label>
          <div className="campo">
            <span className="etiqueta">Digitado por</span>
            <span data-digitador>{digitador}</span>
            <span className="ayuda">Quien digita no es el vendedor del cliente (comisión).</span>
          </div>
          <label className="campo">
            <span className="etiqueta">Documento</span>
            <select
              aria-label="Documento"
              value={b.documento}
              onChange={(e) =>
                despachar({ tipo: 'cabecera', campos: { documento: e.target.value as never } })
              }
            >
              <option value="">
                {b.cliente?.documentoPorDefecto
                  ? `Del cliente (${b.cliente.documentoPorDefecto === 'BOLETA' ? 'boleta' : 'factura'})`
                  : 'Según canal'}
              </option>
              <option value="FACTURA">Factura</option>
              <option value="BOLETA">Boleta</option>
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta">Flete (bruto)</span>
            <input
              aria-label="Flete (bruto)"
              inputMode="numeric"
              placeholder="—"
              value={b.flete?.bruto ?? ''}
              onChange={(e) =>
                despachar({
                  tipo: 'cabecera',
                  campos: {
                    flete: e.target.value
                      ? { bruto: e.target.value, motivo: b.flete?.motivo ?? 'flete de despacho' }
                      : null,
                  },
                })
              }
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Observaciones</span>
            <input
              aria-label="Observaciones"
              value={b.observaciones}
              onChange={(e) =>
                despachar({ tipo: 'cabecera', campos: { observaciones: e.target.value } })
              }
            />
          </label>
          <p className="ayuda">Esc vuelve a la nota. El cálculo se repite solo.</p>
        </Modal>
      )}
      {dialogo?.tipo === 'observacion' && (
        <ObservacionNota
          inicial={b.observaciones ?? ''}
          alCerrar={() => setDialogo(null)}
          alAceptar={(observaciones) => {
            despachar({ tipo: 'cabecera', campos: { observaciones } });
            setDialogo(null);
          }}
        />
      )}
      {dialogo?.tipo === 'vendedor' && k && (
        <MotivoVendedor
          vendedor={
            k.vendedor.permitidos.find((v) => v.id === dialogo.vendedorId) ?? {
              codigo: '',
              nombre: dialogo.vendedorId,
            }
          }
          motivos={k.vendedor.motivosCambio}
          alCerrar={() => setDialogo(null)}
          alAceptar={(cambio) => {
            despachar({
              tipo: 'cabecera',
              campos: { vendedorId: dialogo.vendedorId, cambioVendedor: cambio },
            });
            setDialogo(null);
          }}
        />
      )}
      {dialogo?.tipo === 'quitar' && (
        <Modal
          titulo="Quitar línea"
          ancho="angosto"
          alCerrar={() => setDialogo(null)}
          pie={
            <>
              <button type="button" onClick={() => setDialogo(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="peligro"
                data-autofoco
                onClick={() => {
                  despachar({ tipo: 'quitar', clave: dialogo.clave });
                  setDialogo(null);
                }}
              >
                Quitar
              </button>
            </>
          }
        >
          <p>¿Quitar {dialogo.texto} de la nota? (aún no está confirmada)</p>
        </Modal>
      )}
      {dialogo?.tipo === 'confirmar' && n && (
        <ResumenConfirmar
          nota={n}
          vista={vista}
          procesando={transicion.isPending}
          error={transicion.error}
          alConfirmar={(enLinea) => {
            if (!transicion.isPending) transicion.mutate({ accion: 'confirmar', enLinea });
          }}
          alCerrar={() => {
            if (!transicion.isPending) setDialogo(null);
          }}
        />
      )}
      {avisoCaja === 'abrir' && (
        <Modal
          titulo="Tu caja está cerrada"
          ancho="angosto"
          ubicacion="media"
          alCerrar={() => setAvisoCaja(null)}
          pie={
            <>
              <button type="button" onClick={() => setAvisoCaja(null)}>
                Volver (Esc)
              </button>
              <button type="button" onClick={() => setAvisoCaja('sin-pago')}>
                Seguir sin caja: emitir sin pago
              </button>
            </>
          }
        >
          <AbrirTurno
            empresaId={empresaId}
            enDialogo
            alAbierto={() => {
              setAvisoCaja(null);
              setTimeout(
                () =>
                  contenedorPago.current
                    ?.querySelector<HTMLElement>('[aria-label="Monto 1"]')
                    ?.focus(),
                0,
              );
            }}
          />
        </Modal>
      )}
      {avisoCaja === 'factura' && (
        <Modal
          titulo="Faltan datos para la factura"
          ancho="angosto"
          ubicacion="media"
          alCerrar={() => setAvisoCaja(null)}
          pie={
            <>
              <button type="button" onClick={() => setAvisoCaja(null)}>
                Volver (Esc)
              </button>
              {b.cliente && alAbrirFicha && (
                <button
                  type="button"
                  onClick={() => {
                    setAvisoCaja(null);
                    alAbrirFicha(b.cliente!.id, b.cliente!.razonSocial);
                  }}
                >
                  Completar la ficha (Alt+F)
                </button>
              )}
              <button
                type="button"
                className="primario"
                data-autofoco
                onClick={() => {
                  setAvisoCaja(null);
                  if (emitibleMeson) setDocElegido('BOLETA');
                  else despachar({ tipo: 'cabecera', campos: { documento: 'BOLETA' } });
                }}
              >
                Cambiar a boleta (Enter)
              </button>
            </>
          }
        >
          <p>
            A la ficha de <b>{b.cliente?.razonSocial ?? 'este cliente'}</b> le falta:{' '}
            <b>{faltaParaFactura.join(', ')}</b>. Sin eso el SII no acepta la factura, así que
            todavía no se cobra nada.
          </p>
          <p className="apagado">
            Complétalo en su ficha y vuelve a cobrar con F10, o cambia a boleta si el cliente no la
            necesita.
          </p>
        </Modal>
      )}
      {avisoCaja === 'sin-pago' && (
        <Modal
          titulo="Emitir sin pago"
          ancho="angosto"
          ubicacion="media"
          alCerrar={() => setAvisoCaja(null)}
          pie={
            <>
              <button type="button" onClick={() => setAvisoCaja(null)}>
                Volver (Esc)
              </button>
              <button
                type="button"
                className="primario firma"
                data-autofoco
                disabled={guardar.isPending || transicion.isPending}
                onClick={() => {
                  setAvisoCaja(null);
                  void cobrarYEmitir(true);
                }}
              >
                {puede('ventas.documento.sin_pago')
                  ? 'Emitir sin pago (Enter)'
                  : 'Pedir autorización en línea (Enter)'}
              </button>
            </>
          }
        >
          <p>
            {docMeson === 'FACTURA' ? 'La factura' : docMeson === 'VALE' ? 'El vale' : 'La boleta'}{' '}
            sale <b>sin un pago asociado</b>: queda en la cuenta del cliente y se cobra después.
          </p>
          <p className="apagado">
            {puede('ventas.documento.sin_pago')
              ? 'Tienes el permiso: queda registrado que la emitiste sin pago.'
              : 'Necesita la autorización de un supervisor: se la pides en línea y esperas su firma.'}
          </p>
        </Modal>
      )}
      {esperandoSinPago && n && (
        <EsperandoSinPago
          numeroVisible={n.numeroVisible}
          autorizacion={autorizacionSinPago}
          retirando={retirarSinPago.isPending}
          alRetirar={() =>
            autorizacionSinPago &&
            retirarSinPago.mutate({
              id: autorizacionSinPago.id,
              version: autorizacionSinPago.version,
            })
          }
          alCerrar={() => setEsperandoSinPago(false)}
        />
      )}
      {esperandoFirma && n && (
        <EsperandoFirma
          nota={n}
          firma={firma}
          // En el punto de venta la venta autorizada sigue al cobro: no se ofrece partir otra.
          {...(alNuevaNota && !meson && firma?.resultado === 'APROBADA'
            ? { alNueva: alNuevaNota }
            : {})}
          alCerrar={() => {
            setEsperandoFirma(false);
            if (firma) setFirma(null);
          }}
        />
      )}
      {dialogo?.tipo === 'motivo' && (
        <DialogoMotivo
          accion={dialogo.accion}
          procesando={transicion.isPending || eliminarYGuardar.isPending}
          error={dialogo.accion === 'anular-guardar' ? eliminarYGuardar.error : transicion.error}
          alCerrar={() => !transicion.isPending && !eliminarYGuardar.isPending && setDialogo(null)}
          alAceptar={(motivo) =>
            dialogo.accion === 'anular-guardar'
              ? eliminarYGuardar.mutate(motivo)
              : transicion.mutate({
                  accion: dialogo.accion === 'reservar' ? 'reservar' : dialogo.accion,
                  motivo,
                })
          }
        />
      )}
    </div>
  );
}

/** Lo que el backend anuncia al previsualizar: validaciones, crédito, firmas y stock que no alcanza. */
/**
 * Avisos del cálculo en la banda del cliente (Rodrigo, 2026-10-24: «ya con que salga arriba hay
 * espacio suficiente»): validaciones, la firma que pedirá y el stock que no alcanza, en una línea.
 * El crédito ya lo dice la banda; aquí solo se suma la firma cuando la banda muestra el crédito.
 */
function AvisosCalculo({ vista, conFirma }: { vista: Previsualizacion; conFirma: boolean }) {
  const faltan = (vista.disponibilidad ?? []).filter((d) => !d.alcanza);
  const avisos = [
    ...vista.validaciones.map((v) => (
      <span key={v.codigo} className="texto-malo" data-validacion={v.codigo}>
        {v.mensaje}
        {v.lineas?.length ? ` (líneas ${v.lineas.join(', ')})` : ''}
      </span>
    )),
    ...(conFirma && vista.aprobaciones.requeridos.length
      ? [
          <span key="firmas" data-aviso="firmas">
            Al confirmar: firma{' '}
            {vista.aprobaciones.requeridos.map((a) => NOMBRE_AMBITO[a] ?? a).join(', ')}
          </span>,
        ]
      : []),
    ...(faltan.length
      ? [
          <span key="stock" className="texto-malo" data-aviso="stock">
            Stock insuficiente: {faltan.length} producto{faltan.length > 1 ? 's' : ''} (se guarda
            como borrador, no se confirma)
          </span>,
        ]
      : []),
  ];
  if (!avisos.length) return null;
  return (
    <span className="avisos-banda" aria-label="Avisos del cálculo">
      {avisos}
    </span>
  );
}

function PieTotales({
  totales,
  cargos,
  sumas,
  obsoleto,
  editable,
  lineas,
  despues,
  requiereFirma,
  titulo,
  compacto = false,
}: {
  /** Punto de venta: sin «A lista», «Escalas» ni «Desc.» cuando no aportan (2026-10-16). */
  compacto?: boolean;
  totales: TotalesApi | undefined;
  cargos: CargoNota[] | undefined;
  sumas: { aLista: number | null; escala: number | null; descuentos: number } | null;
  obsoleto: boolean;
  editable: boolean;
  lineas: number;
  despues: number | null;
  requiereFirma: boolean;
  /** «TOTAL NV»; la venta directa se nombra por su documento (2026-11-02). */
  titulo: string;
}) {
  const p = (x: number) => pesos(String(Math.round(x)));
  return (
    <section className="pie-nv" aria-label="Totales">
      <dl className={`izq ${obsoleto ? 'obsoleto' : ''}`}>
        {!totales ? (
          <div className="b">
            <dd className="apagado">
              {editable
                ? 'Los totales los calcula el backend al elegir cliente y productos.'
                : 'Sin datos.'}
            </dd>
          </div>
        ) : (
          <>
            {(!compacto || !!sumas?.escala || !!sumas?.descuentos) && (
              <div
                className="b"
                title="Suma de cantidad × precio de lista de cada presentación (bruto)"
              >
                <dt>A lista</dt>
                <dd className="mono">{sumas?.aLista !== null && sumas ? p(sumas.aLista) : '—'}</dd>
              </div>
            )}
            {(!compacto || !!sumas?.escala) && (
              <div className="b">
                <dt>Escalas</dt>
                <dd className="mono texto-ok">{sumas?.escala ? `−${p(sumas.escala)}` : '—'}</dd>
              </div>
            )}
            {(!compacto || !!sumas?.descuentos) && (
              <div className="b">
                <dt>Desc.</dt>
                <dd className="mono">{sumas?.descuentos ? `−${p(sumas.descuentos)}` : '—'}</dd>
              </div>
            )}
            {(cargos ?? []).map((c, i) => (
              <div className="b" key={i}>
                <dt>{c.tipo === 'FLETE_VENTA' ? 'Flete' : c.descripcion}</dt>
                <dd className="mono">{pesos(c.bruto)}</dd>
              </div>
            ))}
            <div className="b">
              <dt>Neto</dt>
              <dd className="mono">{pesos(totales.neto)}</dd>
            </div>
            {totales.impuestos.map((i) => (
              <div className="b" key={i.codigo}>
                <dt>{i.codigo}</dt>
                <dd className="mono">{pesos(i.monto)}</dd>
              </div>
            ))}
          </>
        )}
        {obsoleto && totales && (
          <div className="totales-aviso">Totales del cálculo anterior · recalculando</div>
        )}
      </dl>
      <div className="der">
        <div className="lbl">
          <span>{titulo}</span>
          <span>
            {lineas} línea{lineas === 1 ? '' : 's'}
          </span>
        </div>
        <div className="v mono" data-total>
          {totales ? pesos(totales.bruto) : '—'}
        </div>
        <div className="cr">
          {despues !== null && (
            <>
              Crédito disponible después:{' '}
              <b className={despues < 0 ? 'texto-malo' : ''}>{pesos(String(despues))}</b>
            </>
          )}
          {requiereFirma && <span className="firma">REQUIERE FIRMA</span>}
        </div>
      </div>
    </section>
  );
}

function ResumenConfirmar({
  nota,
  vista,
  procesando,
  error,
  alConfirmar,
  alCerrar,
}: {
  nota: Nota;
  vista: Previsualizacion | undefined;
  procesando: boolean;
  error: unknown;
  alConfirmar: (enLinea: boolean) => void;
  alCerrar: () => void;
}) {
  const sinPrecio = nota.lineas.filter((l) => !l.precioAplicado);
  // Con restricción (firma): pedir la autorización EN LÍNEA (esperar al supervisor) o dejarla pendiente.
  const requiereFirma =
    (vista?.aprobaciones.requeridos.length ?? 0) > 0 ||
    vista?.credito.resultado === 'REQUIERE_AUTORIZACION';
  const credito = nota.cabecera.condicionPago;
  const disp = vista?.disponibilidad ?? null;
  return (
    <Modal
      titulo={`Confirmar ${nota.numeroVisible}`}
      alCerrar={alCerrar}
      ancho="medio"
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={procesando}>
            Volver (Esc)
          </button>
          {requiereFirma ? (
            <>
              <button
                type="button"
                disabled={procesando || sinPrecio.length > 0}
                onClick={() => alConfirmar(false)}
              >
                Guardar y dejar pendiente de firma
              </button>
              <button
                type="button"
                className="primario"
                data-autofoco
                disabled={procesando || sinPrecio.length > 0}
                onClick={() => alConfirmar(true)}
              >
                {procesando ? 'Procesando…' : 'Solicitar autorización en línea (Enter)'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="primario"
              data-autofoco
              disabled={procesando || sinPrecio.length > 0}
              onClick={() => alConfirmar(false)}
            >
              {procesando ? 'Procesando…' : 'Confirmar nota (Enter)'}
            </button>
          )}
        </>
      }
    >
      <dl className="resumen">
        <dt>Cliente</dt>
        <dd>
          {rut(nota.cabecera.clienteRut)} · {nota.cabecera.clienteRazonSocial}
        </dd>
        <dt>Documento</dt>
        <dd>{nota.cabecera.documentoSolicitado}</dd>
        <dt>Condición</dt>
        <dd>
          {NOMBRE_CONDICION[credito] ?? credito}
          {nota.cabecera.plazoDias ? ` · ${nota.cabecera.plazoDias} días` : ''}
        </dd>
        <dt>Crédito</dt>
        <dd>
          {vista ? (
            <>
              <EstadoCredito resultado={vista.credito.resultado} />{' '}
              {vista.aprobaciones.requeridos.length
                ? 'Quedará en aprobación: la firma una persona autorizada.'
                : 'Se confirma sin firmas.'}
            </>
          ) : (
            'Lo evalúa el backend al confirmar (D-108).'
          )}
        </dd>
        <dt>Líneas</dt>
        <dd>{nota.lineas.length}</dd>
        <dt>Total</dt>
        <dd className="mono fuerte">{pesos(nota.totales.bruto)}</dd>
      </dl>
      <table className="grilla compacta" aria-label="Reserva esperada">
        <thead>
          <tr>
            <th>Producto</th>
            <th className="al-der">Pedido (u. base)</th>
            <th className="al-der">Disponible hoy</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {nota.lineas.map((l) => {
            const d = disp?.find((x) => x.productoId === l.productoId);
            return (
              <tr key={l.numero}>
                <td>
                  {l.sku} · {l.descripcion}
                </td>
                <td className="al-der mono">{decimal(l.cantidadBase)}</td>
                <td className="al-der mono">{d ? decimal(d.disponible) : '—'}</td>
                <td>
                  {d && !d.alcanza && (
                    <span className="insignia tono-aviso">no alcanza para el producto</span>
                  )}
                  {d?.stockPorRevisar && (
                    <span className="insignia tono-aviso" title={STOCK_POR_REVISAR}>
                      stock por revisar
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="ayuda">
        Al confirmar se reserva lo pedido en la bodega de la nota. Sin stock suficiente la nota se
        guarda como borrador pero no se confirma (no se permite stock negativo).
      </p>
      {sinPrecio.length > 0 && (
        <div className="aviso-linea tono-malo">
          Líneas sin precio: {sinPrecio.map((l) => l.numero).join(', ')}. No se confirma sin precio
          (D-60).
        </div>
      )}
      {!!error && <PanelError error={error} />}
    </Modal>
  );
}

/**
 * Firma EN LÍNEA: el vendedor espera al supervisor. La nota se consulta sola (cada 3 s); al firmarse,
 * el diálogo muestra el resultado. «Seguir trabajando» lo cierra: la solicitud queda pendiente y el
 * aviso llega igual cuando el supervisor responda.
 */
function EsperandoFirma({
  nota,
  firma,
  alNueva,
  alCerrar,
}: {
  nota: Nota;
  firma: { resultado: string; motivo: string | null } | null;
  /** Autorizada: Enter parte la siguiente nota en esta pestaña. */
  alNueva?: () => void;
  alCerrar: () => void;
}) {
  const pendiente = nota.solicitudes.find((s) => s.estado === 'PENDIENTE');
  const [segundos, setSegundos] = useState(0);
  useEffect(() => {
    if (firma) return;
    const t = setInterval(() => setSegundos((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [firma]);
  const espera = `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
  return (
    <Modal
      key={firma ? 'resuelta' : 'esperando'}
      titulo={`Autorización en línea · ${nota.numeroVisible}`}
      ancho="angosto"
      ubicacion="media"
      alCerrar={alCerrar}
      pie={
        alNueva ? (
          <>
            <button type="button" onClick={alCerrar}>
              Ver la nota
            </button>
            <button type="button" className="primario" data-autofoco onClick={alNueva}>
              Nueva nota (Enter)
            </button>
          </>
        ) : (
          <button
            type="button"
            className={firma ? 'primario' : undefined}
            data-autofoco
            onClick={alCerrar}
          >
            {firma ? 'Listo (Enter)' : 'Seguir trabajando (queda pendiente)'}
          </button>
        )
      }
    >
      {firma ? (
        <div
          className={`firma-resultado tono-${firma.resultado === 'APROBADA' ? 'ok' : 'malo'}`}
          data-firma={firma.resultado}
        >
          <p className="firma-titulo">
            {firma.resultado === 'APROBADA'
              ? `Autorizada: la ${nota.ventaDirecta ? 'venta' : 'nota'} quedó confirmada`
              : 'Rechazada'}
          </p>
          {firma.motivo && <p>«{firma.motivo}»</p>}
          {firma.resultado !== 'APROBADA' && <p>Ajusta la nota y vuelve a confirmarla.</p>}
        </div>
      ) : (
        <div className="firma-esperando" aria-live="polite" data-esperando-firma>
          <p className="firma-titulo">
            <span className="girando" aria-hidden="true" /> Esperando la firma del supervisor…
          </p>
          {pendiente && (
            <p>
              Firma: <b>{pendiente.ambitos.map((a) => NOMBRE_AMBITO[a] ?? a).join(', ')}</b>
            </p>
          )}
          <p className="apagado">
            El supervisor la ve en «Pendientes de firma». Esperando {espera} · esta pantalla se
            actualiza sola.
          </p>
        </div>
      )}
    </Modal>
  );
}

/**
 * La nota terminó (confirmada, o enviada a firma y pendiente): Enter limpia la pestaña para la
 * siguiente. En el mesón, F10 cobra primero y la pestaña queda limpia al cerrar el cobro. Esc deja
 * la nota a la vista (para revisarla o ir al despacho).
 */
/**
 * Punto de venta: la venta de mesón terminó. Cobrada (recibo RC-…) y con la boleta/factura pedida en
 * el acto. En VALIDACIÓN la emisión está bloqueada: se avisa y la venta queda cobrada. Enter parte la
 * siguiente venta en la misma pestaña.
 */
function VentaMesonTerminada({
  documento,
  venta,
  reintentar,
  alNueva,
  alQuedarse,
  alImprimir,
  errorImpresion,
  vuelto = 0,
  valeNumero,
}: {
  /** 0109: el número del vale emitido (no es DTE). */
  valeNumero?: string;
  alImprimir?: () => void;
  errorImpresion?: unknown;
  /** Efectivo: lo que hay que devolverle al cliente (en grande, como en Random). */
  vuelto?: number;
  documento: string;
  venta: {
    recibo: ReciboRegistrado | null;
    documento: 'EMITIENDO' | 'SOLICITADO' | 'BLOQUEADO_VALIDACION' | 'ERROR';
    error?: unknown;
  };
  reintentar: () => void;
  alNueva?: () => void;
  alQuedarse: () => void;
}) {
  const tipo = documento === 'BOLETA' ? 'Boleta' : documento === 'VALE' ? 'Vale' : 'Factura';
  return (
    <Modal
      key={venta.documento}
      titulo={`Venta de mesón · ${tipo.toLowerCase()}`}
      ancho="angosto"
      ubicacion="media"
      devolverFoco={false}
      alCerrar={alQuedarse}
    >
      <div className="nota-terminada" data-venta-meson={venta.documento}>
        <p className="firma-titulo">
          {venta.documento === 'EMITIENDO' ? 'Pidiendo el documento…' : 'Venta terminada'}
        </p>
        {vuelto > 0 && (
          <div className="vuelto-meson" data-vuelto-final={vuelto}>
            <span>Vuelto al cliente</span>
            <strong className="mono">{pesos(String(vuelto))}</strong>
            <small>
              {composicionVuelto(vuelto)
                .map((c) => `${c.cantidad} × ${pesos(String(c.valor))}`)
                .join(' · ')}
            </small>
          </div>
        )}
        {venta.recibo ? (
          <p>
            Cobrado <b>{pesos(venta.recibo.total)}</b> · recibo{' '}
            <span className="mono">{venta.recibo.numeroVisible}</span>
            {venta.recibo.turno ? ` · turno ${venta.recibo.turno}` : ''}
          </p>
        ) : (
          <p>A crédito: sin cobro en caja.</p>
        )}
        <p data-documento-meson={venta.documento}>
          {tipo}:{' '}
          {venta.documento === 'EMITIENDO'
            ? 'pidiendo…'
            : venta.documento === 'SOLICITADO' && documento === 'VALE'
              ? `N° ${valeNumero ?? ''} emitido (interno, no va al SII); se imprime solo.`
              : venta.documento === 'SOLICITADO'
                ? 'emitida; se imprime sola en unos segundos.'
                : venta.documento === 'BLOQUEADO_VALIDACION'
                  ? 'pendiente. En VALIDACIÓN no se emiten documentos tributarios: la venta quedó cobrada.'
                  : 'no se pudo pedir.'}
        </p>
        {venta.documento === 'ERROR' && !!venta.error && <PanelError error={venta.error} />}
        {!!errorImpresion && <PanelError error={errorImpresion} />}
        <div className="acciones">
          {alImprimir && venta.documento === 'SOLICITADO' && (
            <button type="button" onClick={alImprimir}>
              Imprimir {tipo.toLowerCase()}
            </button>
          )}
          <button type="button" onClick={alQuedarse}>
            Ver la venta (Esc)
          </button>
          {venta.documento === 'ERROR' && (
            <button type="button" onClick={reintentar}>
              Reintentar
            </button>
          )}
          {alNueva && venta.documento !== 'EMITIENDO' && (
            <button type="button" className="primario" data-autofoco onClick={alNueva}>
              Nueva venta (Enter)
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** La emisión de una factura o boleta directa y si la mercadería salió en el acto. */
type EmisionDirecta = {
  estado: 'EMITIENDO' | 'SOLICITADO' | 'ERROR';
  inmediato: boolean;
  dteId?: string;
  error?: unknown;
};

/** Factura o boleta directa: ¿el cliente se lleva la mercadería ahora o queda pendiente de despacho? */
function SalidaMercaderia({
  nombreDoc,
  inmediato,
  alCambiar,
  deshabilitado,
}: {
  nombreDoc: string;
  inmediato: boolean;
  alCambiar: (inmediato: boolean) => void;
  deshabilitado: boolean;
}) {
  return (
    <fieldset
      className="recuadro salida-mercaderia"
      aria-label="Salida de la mercadería"
      disabled={deshabilitado}
    >
      <legend>¿La mercadería sale ahora?</legend>
      <label className="opcion">
        <input
          type="radio"
          name="salida-mercaderia"
          checked={inmediato}
          onChange={() => alCambiar(true)}
        />
        <span>
          <b>Se la lleva ahora</b>
          <small>Queda despachada sola apenas la {nombreDoc} se envía al SII.</small>
        </span>
      </label>
      <label className="opcion">
        <input
          type="radio"
          name="salida-mercaderia"
          checked={!inmediato}
          onChange={() => alCambiar(false)}
        />
        <span>
          <b>Queda pendiente de despacho</b>
          <small>
            Por ejemplo, se paga primero y se despacha después (con su guía) desde «Despachos».
          </small>
        </span>
      </label>
    </fieldset>
  );
}

function EstadoEmisionDirecta({ emision, nombre }: { emision: EmisionDirecta; nombre: string }) {
  return (
    <>
      <p data-emision-directa={emision.estado}>
        {nombre}:{' '}
        {emision.estado === 'EMITIENDO'
          ? 'pidiendo…'
          : emision.estado === 'SOLICITADO'
            ? emision.inmediato
              ? 'solicitada; se emite en segundos y la mercadería queda despachada.'
              : 'solicitada; se emite en segundos. La mercadería queda pendiente de despacho.'
            : 'no se pudo pedir.'}
      </p>
      {emision.estado === 'ERROR' && !!emision.error && <PanelError error={emision.error} />}
    </>
  );
}

/**
 * Emitir la factura o boleta de una venta directa ya confirmada: la autorizada con firma o la que se
 * reabre sin documento (venta 724, 2026-11-02). Es el mismo paso que al confirmar sin firma.
 */
function EmitirVentaDirecta({
  directo,
  emision,
  alEmitir,
  alImprimir,
  errorImpresion,
  alCerrar,
}: {
  directo: 'FACTURA' | 'BOLETA';
  emision: EmisionDirecta | null;
  alEmitir: (inmediato: boolean) => void;
  alImprimir?: () => void;
  errorImpresion?: unknown;
  alCerrar: () => void;
}) {
  const [inmediato, setInmediato] = useState(true);
  const nombreDoc = directo === 'BOLETA' ? 'boleta' : 'factura';
  const pedida = !!emision && emision.estado !== 'ERROR';
  return (
    <Modal
      titulo={`Emitir ${nombreDoc}`}
      ancho="angosto"
      ubicacion="media"
      alCerrar={alCerrar}
      pie={
        <>
          {!pedida && (
            <button
              type="button"
              className="primario"
              data-autofoco
              data-emitir-directo
              onClick={() => alEmitir(inmediato)}
            >
              Emitir {nombreDoc} (Enter)
            </button>
          )}
          {alImprimir && emision?.estado === 'SOLICITADO' && (
            <button type="button" onClick={alImprimir}>
              Imprimir {nombreDoc}
            </button>
          )}
          <button
            type="button"
            className={pedida ? 'primario' : undefined}
            disabled={emision?.estado === 'EMITIENDO'}
            onClick={alCerrar}
          >
            {pedida ? 'Listo' : 'Volver (Esc)'}
          </button>
        </>
      }
    >
      <SalidaMercaderia
        nombreDoc={nombreDoc}
        inmediato={inmediato}
        alCambiar={setInmediato}
        deshabilitado={pedida}
      />
      {emision && <EstadoEmisionDirecta emision={emision} nombre={NOMBRE_DIRECTO[directo]} />}
      {!!errorImpresion && <PanelError error={errorImpresion} />}
    </Modal>
  );
}

function NotaTerminada({
  r,
  cobrable,
  saldo,
  alNueva,
  alCobrar,
  alQuedarse,
  directo,
  sinEmision = false,
  alDespachar,
  emisionDirecta = null,
  alEmitir,
  alImprimir,
  errorImpresion,
}: {
  emisionDirecta?: EmisionDirecta | null;
  /** Factura o boleta directa: emitir diciendo si la mercadería sale ahora o queda pendiente. */
  alEmitir?: (inmediato: boolean) => void;
  alImprimir?: () => void;
  errorImpresion?: unknown;
  directo?: DocumentoDirecto;
  sinEmision?: boolean;
  alDespachar?: () => void;
  r: ResultadoConfirmar;
  cobrable: boolean;
  saldo: string | null;
  alNueva: () => void;
  alCobrar: () => void;
  alQuedarse: () => void;
}) {
  const confirmada = r.estado === 'CONFIRMADA';
  const [inmediato, setInmediato] = useState(true);
  const emite = !!alEmitir && confirmada;
  const nombreDoc = directo === 'BOLETA' ? 'boleta' : 'factura';
  return (
    <Modal
      titulo={
        directo && directo !== 'GUIA'
          ? `${NOMBRE_DIRECTO[directo]} · ${confirmada ? 'grabada' : 'pendiente de firma'}`
          : `${r.numeroVisible} · ${confirmada ? 'confirmada' : 'pendiente de firma'}`
      }
      ancho="angosto"
      ubicacion="media"
      devolverFoco={false}
      alCerrar={alQuedarse}
    >
      <div
        className="nota-terminada"
        data-nota-terminada={r.estado}
        onKeyDown={(e) => {
          if (e.key === 'F10' && cobrable) {
            e.preventDefault();
            alCobrar();
          }
        }}
      >
        <p className="firma-titulo">
          {directo && directo !== 'GUIA'
            ? confirmada
              ? `${NOMBRE_DIRECTO[directo]}: venta grabada`
              : 'Venta guardada: queda pendiente de la firma del supervisor'
            : confirmada
              ? 'Nota confirmada'
              : 'Nota guardada: queda pendiente de la firma del supervisor'}
        </p>
        {emite && (
          <SalidaMercaderia
            nombreDoc={nombreDoc}
            inmediato={inmediato}
            alCambiar={setInmediato}
            deshabilitado={!!emisionDirecta && emisionDirecta.estado !== 'ERROR'}
          />
        )}
        {emisionDirecta && (
          <EstadoEmisionDirecta emision={emisionDirecta} nombre={NOMBRE_DIRECTO[directo!]} />
        )}
        {!!errorImpresion && <PanelError error={errorImpresion} />}
        {r.reserva && (
          <p>
            Reservado {decimal(r.reserva.reservado)} de {decimal(r.reserva.requerido)}
            {!esCero(r.reserva.pendiente) && ` · pendiente ${decimal(r.reserva.pendiente)}`} (unidad
            base)
          </p>
        )}
        {directo && confirmada && !emite && (
          <p data-directo-listo={directo}>
            <strong>
              {directo === 'GUIA'
                ? 'Lista para despachar con guía'
                : `Lista para emitir la ${directo === 'BOLETA' ? 'boleta' : 'factura'}`}
            </strong>
            {sinEmision && (
              <span className="apagado">
                {' '}
                · en preproducción no se despacha ni se emite: el documento real se emite en
                producción.
              </span>
            )}
          </p>
        )}
        {!confirmada && (
          <p className="apagado">
            Queda en «Pendientes de firma». Si te quedas en la nota, te aviso aquí cuando la firmen.
          </p>
        )}
        <div className="acciones">
          {emite && (!emisionDirecta || emisionDirecta.estado === 'ERROR') && (
            <button
              type="button"
              className="primario"
              data-autofoco
              data-emitir-directo
              onClick={() => alEmitir!(inmediato)}
            >
              Emitir {nombreDoc} (Enter)
            </button>
          )}
          {alImprimir && emisionDirecta?.estado === 'SOLICITADO' && (
            <button type="button" onClick={alImprimir}>
              Imprimir {nombreDoc}
            </button>
          )}
          <button type="button" onClick={alQuedarse}>
            {directo && directo !== 'GUIA' ? 'Ver la venta (Esc)' : 'Ver la nota (Esc)'}
          </button>
          {cobrable && (
            <button type="button" onClick={alCobrar}>
              <kbd>F10</kbd>Cobrar{saldo ? ` · saldo ${pesos(saldo)}` : ''}
            </button>
          )}
          {alDespachar && confirmada && (
            <button type="button" onClick={alDespachar}>
              Ir al despacho (guía)
            </button>
          )}
          {(!emite || emisionDirecta?.estado === 'SOLICITADO') && (
            <button type="button" className="primario" data-autofoco onClick={alNueva}>
              {directo
                ? `Nueva ${NOMBRE_DIRECTO[directo].toLowerCase()} (Enter)`
                : 'Nueva nota (Enter)'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ResultadoConfirmacion({ r, alCerrar }: { r: ResultadoConfirmar; alCerrar: () => void }) {
  return (
    <div
      className={`resultado tono-${r.estado === 'CONFIRMADA' ? 'ok' : 'aviso'}`}
      role="status"
      data-resultado={r.estado}
    >
      <div className="panel-error-fila">
        <strong>
          {r.numeroVisible}:{' '}
          {r.estado === 'CONFIRMADA'
            ? 'confirmada'
            : r.estado === 'EN_APROBACION'
              ? 'enviada a aprobación'
              : r.estado}
        </strong>
        {r.credito && (
          <span>
            <EstadoCredito resultado={r.credito.resultado} />{' '}
            {r.credito.motivos.includes('SIN_FUENTE_CXC') && (
              <strong>Esta venta requiere autorización. </strong>
            )}
            {r.credito.motivos.map((m) => MOTIVOS_CREDITO[m] ?? m).join(' · ')}
          </span>
        )}
        {r.reserva && (
          <span data-reserva>
            Reserva: solicitado {decimal(r.reserva.requerido)} · reservado{' '}
            {decimal(r.reserva.reservado)} · pendiente{' '}
            <strong>{decimal(r.reserva.pendiente)}</strong> (unidad base)
          </span>
        )}
        <button type="button" className="boton-icono" onClick={alCerrar} aria-label="Cerrar">
          ✕
        </button>
      </div>
    </div>
  );
}

function DialogoMotivo({
  accion,
  procesando,
  error,
  alCerrar,
  alAceptar,
}: {
  accion: 'aprobar' | 'rechazar' | 'reservar' | 'anular' | 'anular-guardar';
  procesando: boolean;
  error: unknown;
  alCerrar: () => void;
  alAceptar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  // Aprobar = autorizar el bloqueo: el comentario es opcional; queda registrado quién firmó y cuándo.
  const opcional = accion === 'aprobar';
  const listo = opcional
    ? motivo.trim().length === 0 || motivo.trim().length >= 3
    : motivo.trim().length >= 3;
  const titulos = {
    aprobar: 'Autorizar el bloqueo',
    rechazar: 'Rechazar la solicitud (vuelve a borrador)',
    reservar: 'Reservar lo pendiente',
    anular: 'Eliminar la nota de venta',
    'anular-guardar': 'Eliminar y guardar como borrador',
  } as const;
  const elimina = accion === 'anular' || accion === 'anular-guardar';
  return (
    <Modal
      titulo={titulos[accion]}
      ancho="angosto"
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar} disabled={procesando}>
            Cancelar
          </button>
          <button
            type="button"
            className={accion === 'rechazar' || elimina ? 'peligro' : 'primario'}
            disabled={procesando || !listo}
            onClick={() => alAceptar(motivo.trim())}
          >
            {procesando
              ? 'Procesando…'
              : opcional
                ? 'Autorizar'
                : accion === 'anular'
                  ? 'Eliminar'
                  : accion === 'anular-guardar'
                    ? 'Eliminar y guardar'
                    : 'Aceptar'}
          </button>
        </>
      }
    >
      {opcional && <p className="ayuda">Queda registrado que tú autorizaste, con fecha y hora.</p>}
      {elimina && (
        <p className="ayuda">
          {accion === 'anular-guardar'
            ? 'Se crea una copia de la nota en BORRADOR (mismo cliente, productos y condiciones, con fecha de hoy) para usarla de nuevo, y la original se elimina. '
            : ''}
          La nota eliminada queda ANULADA (no se borra del registro) y su reserva de stock se
          libera. No se puede si tiene una factura o boleta vigente, si va en una ODD o si salió
          mercadería. Queda registrado quién la eliminó, cuándo y el motivo.
        </p>
      )}
      <label className="campo">
        {opcional ? 'Comentario (opcional)' : 'Motivo (obligatorio)'}
        <input
          data-autofoco
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && listo && !procesando) alAceptar(motivo.trim());
          }}
        />
      </label>
      {!!error && <PanelError error={error} />}
    </Modal>
  );
}

/**
 * Motivo para vender con un vendedor distinto del asociado al cliente (decisión 2026-10-02). Los
 * motivos los entrega el backend; «Otro» exige observación. Cancelar deja el vendedor como estaba.
 */
function MotivoVendedor({
  vendedor,
  motivos,
  alCerrar,
  alAceptar,
}: {
  vendedor: { codigo: string; nombre: string };
  motivos: MotivoCambioVendedor[];
  alCerrar: () => void;
  alAceptar: (cambio: CambioVendedor) => void;
}) {
  const [motivo, setMotivo] = useState<MotivoCambioVendedor | ''>('');
  const [observacion, setObservacion] = useState('');
  const listo = !!motivo && (motivo !== 'OTRO' || observacion.trim().length >= 3);
  const aceptar = () =>
    listo &&
    alAceptar({
      motivo: motivo as MotivoCambioVendedor,
      ...(observacion.trim() ? { observacion: observacion.trim() } : {}),
    });
  return (
    <Modal
      titulo="Cambiar vendedor"
      ancho="angosto"
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="button" className="primario" disabled={!listo} onClick={aceptar}>
            Aceptar
          </button>
        </>
      }
    >
      <p className="ayuda">
        {vendedor.codigo ? `${vendedor.codigo} · ` : ''}
        {vendedor.nombre} no es el vendedor del cliente: indica el motivo (queda auditado).
      </p>
      <label className="campo">
        Motivo (obligatorio)
        <select
          aria-label="Motivo del cambio de vendedor"
          data-autofoco
          value={motivo}
          onChange={(e) => setMotivo(e.target.value as MotivoCambioVendedor | '')}
        >
          <option value="">Elegir…</option>
          {motivos.map((m) => (
            <option key={m} value={m}>
              {NOMBRE_MOTIVO_VENDEDOR[m] ?? m}
            </option>
          ))}
        </select>
      </label>
      {motivo === 'OTRO' && (
        <label className="campo">
          Observación (obligatoria con «Otro»)
          <input
            aria-label="Observación del cambio de vendedor"
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && aceptar()}
          />
        </label>
      )}
    </Modal>
  );
}

/** La observación de la nota o del documento (sale impresa). Enter acepta; Shift+Enter, otra línea. */
function ObservacionNota({
  inicial,
  alCerrar,
  alAceptar,
}: {
  inicial: string;
  alCerrar: () => void;
  alAceptar: (texto: string) => void;
}) {
  const [texto, setTexto] = useState(inicial);
  return (
    <Modal
      titulo="Observación"
      ancho="medio"
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Volver
          </button>
          <button type="button" className="primario" onClick={() => alAceptar(texto.trim())}>
            Aceptar (Enter)
          </button>
        </>
      }
    >
      <label className="campo">
        <span className="etiqueta">Observación (sale impresa en el documento)</span>
        <textarea
          aria-label="Observación"
          data-autofoco
          rows={3}
          maxLength={1000}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              alAceptar(texto.trim());
            }
          }}
        />
      </label>
    </Modal>
  );
}
